/**
 * L'ISCRIZIONE a un torneo Limulo e il PROFILO DI GIOCO che la precede — logica pura.
 *
 * Nessun `import.meta.env`, nessun `@/env`, nessun `fetch` catturato: le chiamate arrivano
 * come PARAMETRI, come in `lib/limulo/matchActions.ts` e `lib/limulo/auth.ts`, cosi'
 * `tools/verify-limulo.ts` puo' esercitare tutto sotto Node.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * ⚠️ I DUE FATTI CHE DANNO FORMA A TUTTO IL FILE
 * Misurati il 2026-09-06 su `qlash-limulo@origin/staging` = `46404fc` e
 * `qTournaments@origin/main` = `7b773d4`.
 *
 * **(1) Il rifiuto non parla.** Come per le azioni sull'incontro (kb#114 §6, e il cappello
 * di `matchActions.ts`), ogni rifiuto NOMINATO del provider arriva al client come `424
 * E_QLASH_PROVIDER_UNAVAILABLE`: `resolveRuntimeCapability`
 * (`packages/backend/src/modules/app-runtime.ts:1653-1796`) appiattisce l'errore in una
 * stringa nel `catch` e passa al candidato dopo; esauriti i candidati il BFF risponde 424
 * col motivo in `internalDetails`, che `sendPublicError` **logga e non serializza mai**.
 * Qui significa che «iscrizioni chiuse», «sei gia' dentro», «il torneo non esiste» e
 * «manca il Player Tag» sono, a schermo, **la stessa risposta**.
 * ⇒ Contromossa, la stessa di #157: **dopo la POST si RILEGGE**, e si dice cosa il server HA.
 *
 * **(2) 🔴 E QUI SI RILEGGE ANCHE QUANDO LA POST RIESCE.** Non e' zelo: la risposta della
 * POST **non porta `registered`**. `sanitizeRegistrationResult` (`policy.js:411-418`) e' un
 * letterale a tre chiavi — `{tournament, participant, registration}` — mentre `registered`
 * esiste solo in `sanitizeRegistrationStatus` (`policy.js:387-396`), cioe' nella GET. Un 201
 * dice «ho accettato la richiesta», non «risulti iscritto», e con la seconda cosa a schermo
 * al posto della prima l'utente crede a un posto che potrebbe non avere.
 * ⇒ La rilettura e' incondizionata, e `registered` (successo confermato) e
 * `submitted-unconfirmed` (201 senza conferma) sono due esiti DIVERSI di proposito.
 * ─────────────────────────────────────────────────────────────────────────────
 */

import type {
  LimuloProfile,
  LimuloRegistrationResult,
  LimuloRegistrationStatus,
} from '../../types/limulo.ts';
import {
  limuloFailure,
  registrationReasonOf,
  STATUS_SYNC_IDENTITA,
  type LimuloFailure,
  type LimuloRegistrationReason,
} from './errors.ts';
import { torneoGiaAvviato } from './eligibility.ts';
import { outcomeData, outcomeError, type LimuloCallOutcome } from './matchActions.ts';
import {
  LIMULO_WITHDRAWAL_REASONS,
  checkInOf,
  registrationStatusDisplay,
  type LimuloCheckIn,
  type LimuloCheckInReason,
  type LimuloParola,
  type LimuloWithdrawalReason,
} from './model.ts';

// ─────────────────────────────────────────────────────────────────────────────
// 1. Il corpo dell'iscrizione — un solo campo, e il motivo per cui e' uno solo
// ─────────────────────────────────────────────────────────────────────────────

/**
 * `{ members: [il PROPRIO id utente Limulo] }`, oppure `null` se quell'id non ce l'abbiamo.
 *
 * **Perche' `members` e perche' SOLO `members`** [M 2026-09-06]:
 * · senza, `risolviMembri` (`qTournaments core/iscrizioni.ts:1855-1875`) non ha da dove
 *   ricavare l'identita' e risponde `422 iscrizione.identity-unresolved / nessun-membro`;
 * · `userId` mandato dal client **viene buttato**: e' in `FORBIDDEN_CLIENT_FIELDS`
 *   (`policy.js:8-19`) e il BFF ci mette il proprio (`router.js:331`). Mandarlo sarebbe un
 *   campo che non fa niente e che sembra fare qualcosa;
 * · 🔴 **e da kb#237 si manda anche `profileId`, quando c'e' — la riga che qui diceva il
 *   contrario e' scaduta con qTournaments 1.60.0.** Diceva: *«non si manda `profileId` […]
 *   l'identita' che il contratto chiede e' gia' risolta da `members`»*. Dalla 1.60.0 un
 *   torneo con gioco rifiuta l'iscrizione singola senza profilo (`422
 *   iscrizione.profile-required`), e il profilo lo compone il BFF da `profileId`
 *   (`qlash-limulo#118`, `pickProfileId` → `player.read` → verifica di proprieta').
 *   Quale profilo: v. `profiloPerIscrizione`. Il rischio che la riga vecchia citava resta
 *   vero ed e' ora un motivo con copy (`profile.user-mismatch`).
 *
 * ⚠️ `null` NON e' un dettaglio da assorbire con un `?? ''`: senza l'id di `/v1/me` la POST
 * e' un rifiuto garantito, e mandarla comunque produrrebbe un 424 muto al posto di una frase
 * che sa dire cosa manca.
 */
export function registrationBody(
  memberId: string,
  profileId?: string | null,
  acceptedRulesVersion?: number | null,
): LimuloRegistrationBody | null {
  const id = memberId.trim();
  if (id === '') return null;
  const profilo = (profileId ?? '').trim();
  const corpo: LimuloRegistrationBody = profilo === '' ? { members: [id] } : { members: [id], profileId: profilo };
  // 🔴 Il consenso al regolamento (qTournaments 1.93.0): il campo esiste SOLO se la persona ha
  // accettato una versione. Un torneo senza regolamento manda il corpo di sempre, senza la CHIAVE.
  // ⚠️ Chi difende «nessuna chiave» NON sono i tre test a corpo esatto: `toEqual` ignora una chiave con valore
  // `undefined`, e la mutazione «la chiave esiste sempre, `undefined` senza regolamento» li lascia verdi (misurato
  // dal revisore, MR3b). La difendono le asserzioni sulle CHIAVI (`Object.keys`) del banco
  // `iscrizione-con-consenso-al-regolamento` e il test sul filo (`iscrizione-consenso-client-vero`), dove
  // `JSON.stringify` toglierebbe comunque un `undefined` ma un `null` no.
  // ⚠️ Il valore e' un numero, mai una stringa: il motore accetta anche le cifre, ma un tipo solo si verifica.
  return typeof acceptedRulesVersion === 'number' ? { ...corpo, acceptedRulesVersion } : corpo;
}

/** Il corpo della POST d'iscrizione. `acceptedRulesVersion`: v. `registrationBody` e `lib/limulo/consenso.ts`. */
export interface LimuloRegistrationBody {
  members: string[];
  profileId?: string;
  acceptedRulesVersion?: number;
}

/**
 * Quale profilo di gioco mandare con l'iscrizione (kb#237, pezzo 4). Regola decisa da
 * `tma-orc` su delega I-124 (2026-09-23):
 * · **uno** attivo per il gioco del torneo ⇒ si manda quello;
 * · **nessuno** ⇒ non si manda niente, e il `profile.required` del server diventa VERO;
 * · **piu' d'uno** ⇒ sceglie l'utente (la schermata propone il primo);
 * · lista non arrivata ⇒ `sconosciuto`: ~~si iscrive come prima, senza profilo~~.
 *   ⚠️ Dal 2026-09-23 sui tornei con un gioco questo ramo non arriva alla POST: la stessa lista
 *   decide l'idoneita' (`haProfiloPerGioco` ⇒ `null` ⇒ `sconosciuto`, bottone spento).
 *   🔴 **[orc39, 24/09] Le due righe qui sopra erano false insieme, e il reperto lo mostra**: la
 *   lista decide l'idoneita' SOLO quando i requisiti dichiarano `gameProfileRequired: true`
 *   (`eligibilitaDi`, ramo 4) — altrimenti il verdetto e' `idoneo` a lista non arrivata, e la
 *   POST partiva senza profilo contro un server che dalla 1.60.0 lo pretende su OGNI torneo con
 *   gioco. ⇒ `sconosciuto` ora **non arriva mai alla POST**: il pannello aspetta la lista (v.
 *   `attesaProfili`), qualunque cosa dicano i requisiti.
 *
 * L'identificativo e' `profileId` con ripiego su `id`: e' il valore che ha passato la verifica
 * di proprieta' del BFF sul vivo [M consegna-tornei-orc + log BFF letti da limulo-core-orc,
 * 22/09 21:1xZ, `staging-fc30e64`: `Games.player_profile.validated`]. Ha forma di slug,
 * non di ObjectId: non «correggerlo» in `id`.
 */
export type ProfiloPerIscrizione =
  | { kind: 'uno'; profileId: string }
  | { kind: 'nessuno' }
  | { kind: 'piu'; opzioni: { profileId: string; etichetta: string }[] }
  | { kind: 'sconosciuto' };

/**
 * L'identificativo di un profilo da mandare con l'iscrizione: `profileId`, con ripiego su `id`.
 * ⚠️ UNA regola per due sorgenti — la LISTA (`profiloPerIscrizione`) e la CREAZIONE
 * (`createProfileReconciled`), che il BFF sanifica con lo stesso `sanitizePublicProfile`
 * (`policy.js:241-262`). Col profilo implicito (orc41) l'id della creazione va DIRETTO nella POST
 * d'iscrizione: se le due sorgenti avessero regole diverse, lo stesso profilo partirebbe con due id
 * a seconda che l'utente l'abbia creato adesso o un minuto fa.
 */
const idDelProfilo = (p: Partial<LimuloProfile> | null | undefined): string =>
  String(p?.profileId || p?.id || '').trim();

export function profiloPerIscrizione(
  profili: readonly LimuloProfile[] | null | undefined,
  gameId: string,
): ProfiloPerIscrizione {
  if (gameId.trim() === '') return { kind: 'nessuno' };
  if (!Array.isArray(profili)) return { kind: 'sconosciuto' };
  const opzioni = profili
    .filter((p) => p?.gameId === gameId)
    .map((p) => ({
      profileId: idDelProfilo(p),
      etichetta: String(p.displayName || p.name || p.handle || p.profileId || p.id || '').trim(),
    }))
    .filter((o) => o.profileId !== '');
  if (opzioni.length === 0) return { kind: 'nessuno' };
  if (opzioni.length === 1) return { kind: 'uno', profileId: opzioni[0].profileId };
  return { kind: 'piu', opzioni };
}

/**
 * La lista dei profili ha RISPOSTO? (orc39, reperto di Luca del 2026-09-24 21:06Z su `stg-tma`:
 * 422 `profile.required` a chi un profilo CR attivo ce l'ha.)
 *
 * · `pronti`      — la lista e' arrivata (anche vuota), o il torneo non ha un gioco: l'iscrizione
 *                   puo' partire, col profilo che `profiloPerIscrizione` sceglie;
 * · `in-lettura`  — la lista e' in volo: il bottone aspetta;
 * · `illeggibili` — la lista e' in errore e non ne abbiamo una precedente: il bottone aspetta, e
 *                   si offre di riprovare.
 *
 * 🔴 «Non lo so» e' un verdetto (stessa regola di `eligibilitaDi`): una POST mandata senza
 * profilo perche' la lista non e' arrivata prende un `profile.required` che a schermo dice «ti
 * manca il profilo» — a chi il profilo ce l'ha. E' il falso che la sezione Limulo vieta.
 * ⚠️ Si deriva dall'esito di `profiloPerIscrizione`, non da `data` a mano: la regola «quando la
 * lista conta» (torneo con gioco) resta in un posto solo. L'errore conta SOLO senza dati: con una
 * lista gia' arrivata e una rilettura fallita, la lista che abbiamo e' una risposta del server.
 */
export type AttesaProfili = 'pronti' | 'in-lettura' | 'illeggibili';

export function attesaProfili(scelta: ProfiloPerIscrizione, inErrore: boolean): AttesaProfili {
  if (scelta.kind !== 'sconosciuto') return 'pronti';
  return inErrore ? 'illeggibili' : 'in-lettura';
}

/**
 * L'utente ha un profilo per il gioco del torneo? Dalla LISTA dei suoi profili attivi, cioe'
 * dalla stessa fonte e con la stessa regola del profilo che l'iscrizione manda: un pannello
 * che dice «ti manca il profilo» e una POST che ne manda uno non possono leggere due cose
 * diverse.
 *
 * · `true`  — almeno un profilo per quel gioco;
 * · `false` — la lista e' arrivata e per quel gioco non ce n'e' (anche: torneo senza gioco);
 * · `null`  — la lista non e' arrivata ⇒ non lo sappiamo, e l'idoneita' non afferma.
 *
 * 🔴 **Prima si leggeva da `profile` della busta dei requisiti, ed era un'altra cosa**: quel
 * campo e' l'esito di `game.profile.requirements.read` — schema, campi e provider del GIOCO,
 * uguale per tutti — non il profilo dell'utente [M `qlash-limulo@origin/staging` `a9253fa`:
 * `requisitiDiProfilo` in `app-qtournaments/.../iscrizioni.js:69`,
 * `readGameProfileRequirements` in `app-games/runtime-services.js:1794`]. Col provider vivo
 * valeva `true` per chiunque (chi non ha il profilo scopriva il 422 solo premendo), col
 * provider muto `false` per chiunque (anche chi il profilo ce l'ha).
 */
export function haProfiloPerGioco(
  profili: readonly LimuloProfile[] | null | undefined,
  gameId: string,
): boolean | null {
  const scelta = profiloPerIscrizione(profili, gameId);
  if (scelta.kind === 'sconosciuto') return null;
  return scelta.kind !== 'nessuno';
}

/**
 * L'esito dell'iscrizione, dopo la rilettura.
 *
 * 🔴 `registered` e `submitted-unconfirmed` non si fondono. Il primo e' l'unico che
 * autorizza a scrivere «sei iscritto»: e' il server ad averlo detto, in una GET che quel
 * campo ce l'ha. Il secondo e' un 201 che la GET non ha confermato — puo' essere una
 * propagazione lenta o un'iscrizione che non c'e', e a schermo le due cose devono
 * assomigliarsi il giusto: «richiesta inviata», non «sei dentro».
 */
export type LimuloRegistrationOutcome =
  /** POST accettata **e** la rilettura dice `registered: true`. L'unico «sei iscritto». */
  | 'registered'
  /** POST accettata ma la rilettura non lo conferma (o non ha risposto). */
  | 'submitted-unconfirmed'
  /** POST rifiutata, ma il server ti da' per iscritto: il rifiuto era una corsa o un doppio invio. */
  | 'already-registered'
  /** POST rifiutata e il server dice che NON risulti iscritto. Il rifiuto e' reale. */
  | 'refused'
  /** POST rifiutata e la rilettura non e' riuscita: il dubbio vero. */
  | 'refused-unknown'
  /** Non abbiamo l'id utente Limulo: la POST non parte affatto. */
  | 'identity-missing';

export interface LimuloRegistrationReport {
  outcome: LimuloRegistrationOutcome;
  /** Il rifiuto della POST, se c'e' stato — anche quando la rilettura lo ha poi smentito. */
  failure: LimuloFailure | null;
  /**
   * Il motivo NOMINATO dal BFF sul 409 (`details.registrationReason`), o `null` se il rifiuto
   * non e' un 409 o il motivo manca / e' fuori vocabolario. Solo il rifiuto della POST lo porta.
   */
  refusalReason: LimuloRegistrationReason | null;
  /**
   * Lo `status` GREZZO del provider dopo la rilettura (`'registered'`, `'not_registered'`,
   * e quello che il legacy aggiunge di suo). ~~Si mostra, non si traduce: e' il vocabolario
   * di un altro, e mapparlo sul nostro significherebbe dichiarare una corrispondenza che
   * nessuno ha promesso.~~ 🔴 Superata da L-2301 (2026-09-26): i quattro valori noti hanno la
   * loro parola (`registrationStatusDisplay`), e a schermo va quella; il grezzo solo per gli
   * ignoti. Qui resta GREZZO, ed e' giusto: e' il dato, e la parola si sceglie allo schermo
   * (`statoDaAggiungere`, sezione 2-bis).
   */
  serverStatus: string | null;
}

/**
 * Una copy per motivo del 409. `Record` COMPLETO: un motivo nuovo nel vocabolario senza copy e'
 * un errore di build. Le chiavi i18n non sono i codici del BFF (il punto in `window.x` sarebbe
 * un separatore di percorso).
 *
 * ⚠️ Nessuna di queste copy promette un rientro: la causa piu' comune e' l'apertura del
 * check-in, che chiude le iscrizioni (decisione dell'operatore, 13/09) — kb#187.
 *
 * 🔴 ANNOTAZIONE 22/9 — la riga sopra descrive il modello VECCHIO ed e' scaduta: ADR-0153
 * (qTournaments#128, fusa 16:49Z) rende iscrizione e check-in due fasi SOVRAPPONIBILI, e aprire
 * il check-in non chiude piu' niente. La copy di `register.reason.stateClosed` e delle sue
 * gemelle (`register.withdrawFinal`, `withdraw.warning`) e' stata riscritta di conseguenza.
 * PREDICATO: vero finche' `/qtournaments/health` >= 1.61.0 e il modello della finestra (data /
 * chiusura manuale / chiusura check-in, mai dopo l'avvio) non cambia di nuovo.
 *
 * 🔴 `party.member-already-left` e' l'eccezione che NON nomina una causa: il motore lo emette da
 * quattro scritture diverse e una sola porta `party`, quindi la copy dice solo cio' che e' vero
 * in tutte e quattro («questo account risulta gia' passato da questo torneo») e rimanda allo
 * staff. Ne' promette un rientro ne' lo esclude.
 */
export const REGISTRATION_REASON_COPY: Record<LimuloRegistrationReason, string> = {
  'window.not-published': 'register.reason.notPublished',
  'window.before-open': 'register.reason.beforeOpen',
  'window.after-close': 'register.reason.afterClose',
  'window.state-closed': 'register.reason.stateClosed',
  'window.capacity-reached': 'register.reason.capacityReached',
  'party.member-already-left': 'register.reason.memberAlreadyLeft',
  'profile.required': 'register.reason.profileRequired',
  'profile.rejected': 'register.reason.profileRejected',
  'profile.user-mismatch': 'register.reason.profileUserMismatch',
  'identity.unresolved': 'register.reason.identityUnresolved',
  'profile.identity-mismatch': 'register.reason.profileIdentityMismatch',
  // Consenso al regolamento: v. `REGISTRATION_REASONS`. ⚠️ `rules.changed` NON arriva solo a schermo con la
  // sua frase: il componente rilegge il dettaglio e, se la versione e' cambiata, dice la frase del punto 2 del
  // disegno CON la versione (`register.rules.changed`) — la stessa frase, non due. Questa chiave resta per
  // quando la rilettura non aggiunge niente (stessa versione o rilettura fallita).
  'rules.consent-required': 'register.reason.rulesConsentRequired',
  'rules.changed': 'register.reason.rulesChanged',
  // I premi non verificabili (qTournaments 1.99.1, limulo#196/#197): un guasto PASSEGGERO, la frase dice di
  // riprovare — niente gergo, niente «424» — e il bottone d'iscrizione resta usabile, perche' lo decide
  // `eligibilita` (lo stato del torneo letto dal server) e non l'esito del rifiuto. Nessuna riprova automatica.
  'registration.prizes-unverifiable': 'register.reason.prizesUnverifiable',
  // Il profilo non verificabile (qT `iscrizione.profilo-non-verificabile`, `qlash-limulo#207`, fusa e servita su staging il
  // 05/10): stesso comportamento dei premi — un guasto PASSEGGERO, la frase dice di riprovare, niente gergo, niente «424», e il
  // bottone resta usabile (lo decide `eligibilita`, non l'esito del rifiuto); nessuna riprova automatica. ⚠️ La frase e'
  // NEUTRA su DI CHI e' il profilo: dalla qT 1.114.0 (staging `ae0ddb2`, `qlash-limulo#209`) puo' essere di un compagno di
  // gruppo, e «il tuo profilo» sarebbe falso.
  'profile.unverifiable': 'register.reason.profileUnverifiable',
};

/**
 * L'iscrizione, con la rilettura SEMPRE — anche in caso di successo (v. il fatto (2) in cima).
 */
export async function registerReconciled(input: {
  register: (body: LimuloRegistrationBody) => Promise<LimuloCallOutcome<LimuloRegistrationResult | null>>;
  /** `GET /v1/tournaments/:id/registration` — l'unica risposta che porta `registered`. */
  readRegistration: () => Promise<LimuloCallOutcome<LimuloRegistrationStatus | null>>;
  /** `me.user.id` di `/v1/me` — l'ObjectId utente Limulo, MAI il `sub` Logto. */
  memberId: string;
  /** Il profilo scelto da `profiloPerIscrizione`; assente/`null` = si iscrive senza. */
  profileId?: string | null;
  /**
   * La versione del regolamento che la persona ha visto e accettato (`lib/limulo/consenso.ts`); assente/`null`
   * = il torneo non chiede il consenso e il corpo e' quello di sempre.
   */
  acceptedRulesVersion?: number | null;
}): Promise<LimuloRegistrationReport> {
  const body = registrationBody(input.memberId, input.profileId, input.acceptedRulesVersion);
  if (body === null) {
    return { outcome: 'identity-missing', failure: null, refusalReason: null, serverStatus: null };
  }

  const esito = await input.register(body);
  const errore = outcomeError(esito);

  const riletto = await input.readRegistration();
  const stato = outcomeData(riletto);
  // ⚠️ `undefined` (rilettura fallita) e `null` (2xx con corpo vuoto) si trattano uguale:
  // in entrambi i casi il server non ha detto niente su di noi, e dedurne «non iscritto»
  // sarebbe un'affermazione al posto suo.
  const letto = stato !== undefined && stato !== null ? stato : null;
  // `typeof`: uno `status` assente o non stringa (fuori contratto) e' «non l'ha detto», come il vuoto.
  // Senza, a schermo arrivava «Per il server risulti: .» (revisione di #460, seconda passata, 3).
  const serverStatus = letto !== null && typeof letto.status === 'string' && letto.status !== '' ? letto.status : null;

  if (errore === undefined) {
    return {
      outcome: letto !== null && letto.registered ? 'registered' : 'submitted-unconfirmed',
      failure: null,
      refusalReason: null,
      serverStatus,
    };
  }

  const failure = limuloFailure(errore);
  const refusalReason = registrationReasonOf(errore);
  if (letto === null) {
    return { outcome: 'refused-unknown', failure, refusalReason, serverStatus: null };
  }
  return {
    outcome: letto.registered ? 'already-registered' : 'refused',
    failure,
    refusalReason,
    serverStatus,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// 2. Il ritiro — che ora c'e', e resta DEFINITIVO
// ─────────────────────────────────────────────────────────────────────────────

/**
 * ✅ **Il ritiro ha una rotta sulla facciata, dal mirror qTournaments 1.18.2.**
 * [M 2026-09-08, `qlash-limulo@origin/staging` = `74052bb`, rimisurato in modo indipendente
 * da due seggi]: `qlash.tournaments.registration.withdraw` e' nel catalogo
 * (`endpoint-catalog.js:726`, `DELETE /v1/tournaments/:tournamentId/registration`), e'
 * MONTATA (`router.js:2104`) ed e' implementata nel motore
 * (`app-qtournaments/app/core/iscrizioni.js`).
 *
 * ⚠️ **Questa costante e' stata `false` piu' a lungo del dovuto**, e la copy che le stava
 * accanto ha continuato a dire all'utente «la rotta non esiste sulla facciata» dopo che la
 * rotta esisteva. Il difetto non e' stato il `false`: e' che la frase mostrata a schermo
 * ripeteva un fatto misurato una volta sola, e nessuno rilegge una frase che non fallisce.
 * ⇒ Il gate (`tools/verify-limulo.ts`) tiene ora l'asserzione nel verso opposto: se qualcuno
 * TOGLIE l'endpoint di ritiro da `services/limuloApi.ts`, il rosso dice «rispegni il bottone».
 *
 * 🔴 **E IL RITIRO E' DEFINITIVO — questa meta' non e' cambiata.** [qTournaments C22, aperta
 * al 2026-09-08; finestra dichiarata da Gionko 11-14] **Chi si ritira non puo' iscriversi di
 * nuovo allo stesso torneo**: l'indice unique sull'iscrizione non filtra sullo stato, quindi
 * la riga ritirata occupa il posto per sempre. La conferma deve dirlo, e la copy che lo dice
 * si conserva finche' C22 non e' chiusa — accendere il bottone non e' un motivo per
 * riscrivere ottimista l'avvertimento.
 *
 * ✅ **E DA kb#267 IL RIFIUTO HA UN NOME** (riga precedente: «esiti nominati del dominio che arrivano
 * MUTI, 424»). [M `qlash-limulo@origin/staging` = `7e4d86e`] Il bordo proietta il codice del motore in
 * `details.withdrawalReason` (`withdrawal-refusal.js`, vocabolario CHIUSO dei cinque
 * `LIMULO_WITHDRAWAL_REASONS`), con lo stato del motore: `squalificato` 403, `non-iscritto` 404,
 * `gia-ritirato`/`finestra-chiusa`/`torneo-finito` 409, e (`qlash-limulo#205`) `non-capitano` 403 e
 * `carica-non-verificabile` 424. Lo legge `withdrawalReasonOf`.
 */
export const LIMULO_WITHDRAW_AVAILABLE = true as const;

/**
 * L'esito del ritiro, dopo la rilettura.
 *
 * 🔴 Non e' il gemello speculare di `LimuloRegistrationOutcome`, e la differenza sta in un
 * posto solo: **non esiste `identity-missing`**. Il `DELETE` parte con una sorgente VUOTA e
 * la parte la deriva il server dall'attore (`router.js:2109-2116`), quindi non c'e' un id da
 * avere o non avere prima di chiamare. Copiare qui quell'esito avrebbe aggiunto un ramo che
 * il contratto non puo' produrre.
 */
export type LimuloWithdrawOutcome =
  /** DELETE accettato **e** la rilettura dice `registered: false`. L'unico «sei fuori». */
  | 'withdrawn'
  /** DELETE accettato ma la rilettura non lo conferma (o non ha risposto). */
  | 'submitted-unconfirmed'
  /** DELETE rifiutato, ma il server ti da' per NON iscritto: era una corsa o un doppio tap. */
  | 'already-out'
  /** DELETE rifiutato e il server ti da' ancora dentro. Il rifiuto e' reale. */
  | 'refused'
  /** DELETE rifiutato e la rilettura non e' riuscita: il dubbio vero. */
  | 'refused-unknown';

export interface LimuloWithdrawReport {
  outcome: LimuloWithdrawOutcome;
  /** Il rifiuto del DELETE, se c'e' stato — anche quando la rilettura lo ha poi smentito. */
  failure: LimuloFailure | null;
  /** Il motivo NOMINATO del rifiuto (`details.withdrawalReason`, kb#267), o `null`. */
  refusalReason: LimuloWithdrawalReason | null;
  /**
   * Lo `status` GREZZO del provider dopo la rilettura. ~~Si mostra, non si traduce.~~ 🔴 Superata
   * da L-2301: come per l'iscrizione, a schermo va la parola (`statoDaAggiungere`).
   */
  serverStatus: string | null;
}

/**
 * Il ritiro, con la rilettura SEMPRE.
 *
 * ⚠️ **La risposta del DELETE porta gia' `registered`** (`sanitizeRegistrationStatus`,
 * `router.js:2123`) — eppure si rilegge lo stesso, e il motivo e' il fatto (1) in cima: il
 * ramo che conta non e' quello che riesce, e' quello che viene RIFIUTATO. Un 424 muto non
 * dice se il ritiro non e' passato o se era gia' passato, e le due frasi da mostrare sono
 * diverse. Una funzione che rileggesse solo «quando serve» avrebbe due percorsi da provare
 * al posto di uno.
 */
/**
 * Il motivo del rifiuto del RITIRO, o `null`. Legge SOLO `data.details.withdrawalReason`, mai il
 * messaggio; un valore fuori vocabolario, un tipo sbagliato o l'assenza tornano `null` — e resta la
 * frase generica di prima.
 *
 * ⚠️ Il cancello sullo stato ammette **403, 404, 409 e 422**: il motore dichiara 403 per
 * `ritiro.squalificato` e 404 per `ritiro.non-iscritto` (`rotte/contratto.js` del mirror, righe
 * 238-241), e il bordo promuove ogni 4xx tranne il 401 (`router.js:227`). Un cancello sul solo 409
 * butterebbe due motivi su cinque: e' la lezione di `registrationReasonOf` (kb#237). La guardia vera
 * resta il vocabolario chiuso; lo stato serve a non leggere un motivo dentro cio' che non e' un
 * rifiuto di dominio (un 424 qualunque, un 5xx, la rete).
 *
 * 🔴 **Il 424 vale per UN motivo solo**: `ritiro.carica-non-verificabile` (qTeams non risponde, e il permesso
 * non si presume — `qlash-limulo#205`, `withdrawal-refusal.js:9-12`, status 424 in `tests/qlash-tournament-facade.test.ts:74`
 * [M `qlash-limulo@origin/staging` = `7b85936`]). E' lo stesso cancello per MOTIVO di `MOTIVI_SOLO_SU_424`
 * dell'iscrizione (`errors.ts`): un 424 con un altro motivo resta muto (e' di norma «nessun provider»), e
 * `carica-non-verificabile` con un 4xx diverso dal 424 non e' come il bordo lo manda.
 */
export function withdrawalReasonOf(error: unknown): LimuloWithdrawalReason | null {
  const envelope = error !== null && typeof error === 'object' ? (error as Record<string, unknown>) : {};
  const status = envelope.status as number;
  if (![403, 404, 409, 422, 424].includes(status)) return null;
  const data = envelope.data !== null && typeof envelope.data === 'object' ? (envelope.data as Record<string, unknown>) : {};
  const details =
    data.details !== null && typeof data.details === 'object' ? (data.details as Record<string, unknown>) : {};
  const reason = LIMULO_WITHDRAWAL_REASONS.find((known) => known === details.withdrawalReason) ?? null;
  return (reason === 'ritiro.carica-non-verificabile') === (status === 424) ? reason : null;
}

export async function withdrawReconciled(input: {
  withdraw: () => Promise<LimuloCallOutcome<LimuloRegistrationStatus | null>>;
  /** `GET /v1/tournaments/:id/registration` — la fonte di `registered` dopo il fatto. */
  readRegistration: () => Promise<LimuloCallOutcome<LimuloRegistrationStatus | null>>;
}): Promise<LimuloWithdrawReport> {
  const esito = await input.withdraw();
  const errore = outcomeError(esito);

  const riletto = await input.readRegistration();
  const stato = outcomeData(riletto);
  // Stessa regola dell'iscrizione: `undefined` (rilettura fallita) e `null` (2xx vuoto) sono
  // entrambi «il server non ha detto niente su di me», e dedurne uno stato sarebbe
  // un'affermazione al posto suo.
  const letto = stato !== undefined && stato !== null ? stato : null;
  // `typeof`: uno `status` assente o non stringa (fuori contratto) e' «non l'ha detto», come il vuoto.
  // Senza, a schermo arrivava «Per il server risulti: .» (revisione di #460, seconda passata, 3).
  const serverStatus = letto !== null && typeof letto.status === 'string' && letto.status !== '' ? letto.status : null;

  if (errore === undefined) {
    return {
      outcome: letto !== null && letto.registered === false ? 'withdrawn' : 'submitted-unconfirmed',
      failure: null,
      refusalReason: null,
      serverStatus,
    };
  }

  const failure = limuloFailure(errore);
  const refusalReason = withdrawalReasonOf(errore);
  if (letto === null) return { outcome: 'refused-unknown', failure, refusalReason, serverStatus: null };
  return {
    outcome: letto.registered ? 'refused' : 'already-out',
    failure,
    refusalReason,
    serverStatus,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// 2-bis. Lo stato del server nella frase d'esito — la parola, e solo quando aggiunge qualcosa
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Gli stati d'iscrizione che la frase d'esito AFFERMA gia' da sola, per esito: sotto la frase la riga
 * dello stato compare per ogni altro valore. `[]` = la frase non afferma nessuno stato, quindi
 * qualunque stato riletto si dice. `null` = l'esito non ha una rilettura da dire: `identity-missing`
 * non arriva a rileggere, e sui `refused-unknown` la rilettura e' fallita (`serverStatus` e' sempre
 * `null`). `Record` COMPLETO, come `ESITO_COPY`: un esito nuovo senza una riga qui e' un errore di
 * build.
 *
 * I valori sono quelli della busta (`registrationStatusDisplay`): «risulti iscritto» dice
 * `registered`; «non risulti (piu') iscritto» e «risultavi gia' fuori» dicono `not_registered` e,
 * dopo un ritiro, anche `withdrawn` (e' lo stato che il motore scrive ritirando: «Pippo Cap»,
 * 2026-09-12, `registered: true` → `status: "withdrawn"`).
 * ⚠️ `withdrawn` NON e' fra i detti di «non risulti ANCORA iscritto» (l'iscrizione non confermata):
 * a chi prova a rientrare, sapere che il server lo tiene ancora per ritirato e' un'informazione.
 * 🔑 **I rifiuti CONTANO** (revisione di #460, nota 3): «Iscrizione rifiutata, e il server dice che
 * non risulti iscritto» afferma `not_registered`, «il server ti da' ancora iscritto» afferma
 * `registered`. E sotto un rifiuto «Squalificato» pesa piu' che altrove: e' la differenza fra
 * riprovare e scrivere allo staff. Vale anche quando al posto della frase generica c'e' quella del
 * MOTIVO (`register.reason.*`, `withdrawal.reason.*`, il 409 nudo): la riga dice lo stato RILETTO,
 * che e' un'altra fonte del motivo. ⚠️ Non tutte quelle frasi affermano il lato dell'esito: fra i
 * motivi del ritiro, `ritiro.non-iscritto` afferma il contrario di `refused` (che vuol dire «riletto
 * iscritto»). Nativamente la coppia non si produce — con quel motivo la rilettura da' `already-out` —
 * ~~e se capita (una corsa, dati incoerenti) la riga «Iscritto» sotto e' proprio l'informazione che
 * serve.~~ 🔴 **FALSO, e mostrato invece di cancellato** (revisione di #460, terza passata, 1): se
 * capita, la riga NON compare. `registered` e' fra i detti di `refused`, quindi la frase del motivo
 * («Non risulti iscritto a questo torneo») resta sola, contraddetta da una rilettura che non si vede.
 * Accettato perche' la coppia non e' nativa; se diventasse reale, la cura e' passare `[]` quando il
 * motivo e' `ritiro.non-iscritto`. (Revisione di #460, seconda passata, 2.)
 * ⚠️ E i motivi che contraddicono «iscritto» sono TRE, non uno: anche `ritiro.gia-ritirato` e
 * `ritiro.squalificato`, con la stessa riga che non compare. La cura, se servisse, vale per tutti e tre
 * (`finestra-chiusa` e `torneo-finito` invece sono coerenti con «iscritto»). (Seguito di #460, in #515.)
 * 🔑 **Il ritiro non confermato non afferma niente** (revisione di #460, nota 1): la rilettura puo'
 * essere fallita, e allora «risulti ancora iscritto» era un'affermazione al posto del server. La
 * frase ora dice «il server non ha ancora confermato il ritiro», e un `registered` letto davvero
 * compare sotto, come riga.
 */
export const ESITO_ISCRIZIONE_DICE: Record<LimuloRegistrationOutcome, readonly string[] | null> = {
  registered: ['registered'],
  'already-registered': ['registered'],
  'submitted-unconfirmed': ['not_registered'],
  refused: ['not_registered'],
  'refused-unknown': null,
  'identity-missing': null,
};

/** Come `ESITO_ISCRIZIONE_DICE`, per il ritiro. */
export const ESITO_RITIRO_DICE: Record<LimuloWithdrawOutcome, readonly string[] | null> = {
  withdrawn: ['withdrawn', 'not_registered'],
  'submitted-unconfirmed': [],
  'already-out': ['withdrawn', 'not_registered'],
  refused: ['registered'],
  'refused-unknown': null,
};

/**
 * Lo stato d'iscrizione da AGGIUNGERE alla frase d'esito — o `null`, e allora la frase basta da
 * sola. L-2301 («parole per gli stati noti, il codice grezzo solo per quelli che non conosciamo»),
 * qlash-telegram-app#454 punto 1.
 *
 * Fino a qui ogni frase finiva con lo `status` grezzo fra parentesi: «Risulti iscritto. Il server
 * lo conferma (registered).» Passarci la parola non bastava: «…lo conferma (Iscritto)» ripete la
 * frase. ⇒ la regola, in quattro casi:
 *  · il server non l'ha detto (`null`) ⇒ niente. Prima si stampava «(—)»;
 *  · lo stato e' uno di quelli che la frase gia' afferma ⇒ niente;
 *  · lo stato e' NOTO e dice DI PIU' ⇒ la sua parola: «Squalificato» sotto «non risulti ancora
 *    iscritto, riprova» e' la differenza fra riprovare e scrivere allo staff;
 *  · lo stato e' IGNOTO alla TMA ⇒ il grezzo, com'e' (L-2301 punto 2: un valore nuovo non sparisce).
 * ⚠️ Il confronto e' sul valore GREZZO, mai sulla parola: due valori diversi con la stessa
 * traduzione non devono potersi annullare a vicenda.
 */
export function statoDaAggiungere(
  detti: readonly string[] | null,
  serverStatus: string | null,
): LimuloParola | null {
  if (detti === null || typeof serverStatus !== 'string' || serverStatus === '') return null;
  if (detti.includes(serverStatus)) return null;
  return registrationStatusDisplay(serverStatus);
}

// ─────────────────────────────────────────────────────────────────────────────
// 3. Il profilo di gioco: il modulo si legge dal server, non si scrive a mano
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Un campo del modulo, come `sanitizePublicProfileSchema` lo consegna
 * (`app-games/runtime-services.js:1758-1762`).
 *
 * ⚠️ **`GET /v1/games/:gameId/profile-requirements` e' l'UNICA rotta scritta della sezione
 * SENZA facciata**: `router.js:2218-2235` fa `sendOk(res, req, data)` sul dato grezzo. La
 * forma e' quindi al 100% del provider e puo' cambiare senza che il BFF cambi — stessa
 * classe di `standings`. Per questo si legge in modo difensivo campo per campo invece di
 * fidarsi di una interfaccia: ADR-0020 dice che il contratto diventera' quello che noi
 * eserciteremo, e non c'e' motivo di fingere una forma che nessuno ha promesso.
 */
export interface LimuloProfileField {
  key: string;
  type: string;
  label: string;
  description: string;
  required: boolean;
  placeholder: string;
  options: readonly { value: string; label: string }[];
}

/**
 * Le chiavi che il modulo **non chiede**, e che non contano come mancanti.
 *
 * · `userId` — e' in `FORBIDDEN_CLIENT_FIELDS` (`policy.js:8-19`): il BFF lo cancella e ci
 *   mette il proprio (`buildSafeProfilePayload`, `router.js:335-338`). Un input garantito
 *   scartato e' peggio di un input assente: chiede all'utente un gesto che non fa niente.
 * · `gameId` — lo porta il contesto (il torneo da cui si arriva), non l'utente.
 *
 * ⚠️ **Si filtra per CHIAVE, non per `type`.** Sarebbe stato altrettanto efficace oggi
 * (i due campi hanno `type: 'user'` e `type: 'game'`), ma quei tipi sono un'OSSERVAZIONE
 * del vocabolario di un provider, mentre `userId` scartato dal BFF e' un CONTRATTO
 * misurato. ADR-0049: si asseriscono contratti, non osservazioni — e un filtro per tipo
 * butterebbe un campo vero il giorno che un provider chiamasse `type: 'game'` qualcosa che
 * l'utente deve invece scegliere.
 */
// · `status` — lo stato del profilo (attivo, …) ha un DEFAULT del provider (`active`,
//   `app-game-clash-royale/backend/provider.js`, campo non obbligatorio) e non e' una scelta
//   dell'utente. Luca, 2026-09-23 (prova su stg-tma): «il campo Status (Attivo, ecc.) non ha
//   senso offrirlo lato utente». Non renderlo ⇒ non parte nel corpo (`profileFormBody` manda
//   solo i campi resi): alla creazione vale il default, sul Relink resta quello di prima.
export const LIMULO_PROFILE_SERVER_FIELDS: readonly string[] = ['userId', 'gameId', 'status'];

/**
 * Il provider di profilo dei giochi SENZA integrazione (eFootball e i prossimi): locale in
 * `app-games` (`profile-providers/generic.js`), nessuna verifica, `validationMode: 'sync'`.
 */
export const LIMULO_PROFILE_TYPE_GENERIC = 'generic';

/**
 * I tipi di profilo dei giochi SENZA integrazione: nessuna API, nessuna partita esterna, si gioca
 * nel gioco e si referta qui (ADR-0138). Non basta `generic`: da limulo#152 (@phomea, kb#293 §2,
 * decisione di Luca `L-2270` del 2026-09-26) eFootball ha un profilo **dedicato** locale,
 * `efootball`, con l'ID di gioco obbligatorio, ed e' ancora un gioco senza integrazione.
 * ⚠️ Riconoscerlo dal SOLO `generic` farebbe sparire «Come si gioca» e tornare «non sappiamo
 * aprirlo» il giorno in cui il record passa al profilo dedicato, senza nessun errore.
 * ⇒ Un gioco senza integrazione NUOVO, con un profilo suo, si aggiunge QUI.
 */
export const LIMULO_PROFILE_TYPES_SENZA_INTEGRAZIONE: ReadonlySet<string> = new Set([
  LIMULO_PROFILE_TYPE_GENERIC,
  'efootball',
]);

/** Vero se il tipo di profilo del gioco e' quello di un gioco senza integrazione. */
export function giocoSenzaIntegrazione(profileType: string | null | undefined): boolean {
  return LIMULO_PROFILE_TYPES_SENZA_INTEGRAZIONE.has((profileType ?? '').trim());
}

/**
 * Le chiavi da gestionale che il modulo del GIOCATORE non chiede sul `generic`, se lo schema non
 * le pretende. Solo sul generic: e' il suo schema a essere scritto per il backoffice.
 *
 * Lo schema `generic` e' scritto per il backoffice (la sua descrizione dice «usati dal
 * gestionale»): accanto a nome e piattaforma offre `level`, `rank` e `rating`, tutti
 * facoltativi. Un giocatore non dichiara il proprio rank: offrirglielo e' un campo che nessuno
 * dovrebbe compilare. ⚠️ Solo se NON richieste: una chiave che lo schema pretende si chiede
 * sempre, altrimenti la POST prenderebbe un rifiuto certo (v. `missingRequired`).
 */
export const LIMULO_PROFILE_STAFF_FIELDS: readonly string[] = ['level', 'rank', 'rating'];

/**
 * L'ID di gioco DEDICATO di un profilo senza integrazione (`efootball`, limulo#152, `L-2270`):
 * `externalPlayerId`, richiesto, ed e' il campo che lo staff legge in disputa (kb#292 §2b). Quando
 * lo schema lo porta, il `handle` facoltativo che eredita dal `generic` («ID nel gioco» nelle
 * nostre etichette) sarebbe un secondo campo «ID» accanto al primo: non si chiede, se lo schema
 * non lo pretende.
 */
export const LIMULO_PROFILE_ID_DEDICATO = 'externalPlayerId';

export interface LimuloProfilePlan {
  /** I campi da rendere, gia' privi di quelli che il server si mette da solo. */
  fields: LimuloProfileField[];
  /**
   * 🔴 Le chiavi che lo schema dichiara RICHIESTE e per cui non esiste un campo da rendere.
   *
   * Non e' un caso di scuola: `sanitizePublicProfileSchema` filtra via i campi
   * `hidden !== true` (`:1758`) ma **non tocca `required`**, quindi una chiave puo' restare
   * obbligatoria dopo che il suo campo e' sparito. Il modulo non potrebbe soddisfarla, e la
   * POST prenderebbe un 424 muto — cioe' l'utente scoprirebbe premendo che non poteva.
   * ⇒ Chi ha questa lista non vuota lo dice PRIMA, e non manda niente.
   */
  missingRequired: string[];
  title: string;
  description: string;
  /** `'sync' | 'async' | ''` — `async` significa che la verifica non e' finita al 201. */
  validationMode: string;
  /** `null` = il provider non l'ha dichiarato; non si assume `false`. */
  authRequired: boolean | null;
  gameName: string;
  /** Il tipo di profilo del gioco (`clash-royale`, `generic`, …); `''` se lo schema tace. */
  profileType: string;
}

const record = (v: unknown): Record<string, unknown> =>
  v !== null && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : {};
const str = (v: unknown): string => (typeof v === 'string' ? v : '');

const optionsOf = (raw: unknown): { value: string; label: string }[] => {
  if (!Array.isArray(raw)) return [];
  return raw.map((o) => {
    // Un'opzione puo' essere una stringa nuda o `{value,label}`: il sanitizer la passa
    // com'e' (`options: Array.isArray(field.options) ? field.options : undefined`).
    if (typeof o === 'string') return { value: o, label: o };
    const r = record(o);
    const value = str(r.value);
    return { value, label: str(r.label) || value };
  });
};

/**
 * Il piano del modulo, derivato dalla risposta di `profile-requirements`.
 *
 * 🔴 **Un campo e' richiesto se lo dice `schema.required` OPPURE `field.required`.** Le due
 * fonti esistono entrambe (`:1744` e `:1751`) e possono divergere. Si prende l'UNIONE, non
 * l'intersezione, per un'asimmetria di costo: chiedere un campo che si scopre facoltativo
 * costa all'utente un input in piu'; **non** chiedere un campo davvero richiesto costa un
 * rifiuto che — per il fatto (1) in cima — non dice quale campo mancava.
 */
export function profileFormPlan(raw: unknown): LimuloProfilePlan {
  const r = record(raw);
  const schema = record(r.schema);
  const provider = record(r.provider);
  const game = record(r.game);

  const requiredKeys = new Set(
    (Array.isArray(schema.required) ? schema.required : []).map(str).filter((k) => k !== ''),
  );

  // Prima il PROVIDER: `sanitizePublicProfileSchema` riempie `schema.profileType` con
  // `'generic'` quando manca (`runtime-services.js:1794`), quindi lo schema da solo farebbe
  // passare per generic un provider esterno che se l'e' dimenticato.
  const profileType = str(provider.id) || str(provider.key) || str(schema.profileType);
  // ⚠️ Non `=== generic`: il profilo DEDICATO di un gioco senza integrazione (`efootball`) eredita lo
  // schema del generic, `level`/`rank`/`rating` compresi (limulo#152). Riconoscerlo dal solo
  // `generic` rimetteva quei campi nel modulo al cambio del record (revisione di #461).
  const generico = giocoSenzaIntegrazione(profileType);

  const rawFields = Array.isArray(schema.fields) ? schema.fields : [];
  const haIdDedicato = rawFields.some((f) => str(record(f).key) === LIMULO_PROFILE_ID_DEDICATO);
  const fields: LimuloProfileField[] = [];
  const resi = new Set<string>();

  for (const f of rawFields) {
    const fr = record(f);
    const key = str(fr.key);
    if (key === '') continue;
    if (LIMULO_PROFILE_SERVER_FIELDS.includes(key)) continue;
    resi.add(key);
    if (
      generico &&
      (LIMULO_PROFILE_STAFF_FIELDS.includes(key) || (haIdDedicato && key === 'handle')) &&
      fr.required !== true &&
      !requiredKeys.has(key)
    ) {
      continue;
    }
    // ⚠️ `readOnly` esce dal sanitizer come `Boolean(field.readOnly)`: e' una dichiarazione,
    // e i campi che la portano NON entrano in `fields` — per questo `LimuloProfileField` non
    // ha piu' quel campo: sarebbe uscito sempre `false`, cioe' un dato che non varia mai
    // travestito da informazione.
    // non un'osservazione. Un campo che il provider dichiara non scrivibile non si rende —
    // ma resta in `resi`, perche' la sua chiave e' comunque coperta dallo schema e non e'
    // «mancante» nel senso che conta qui sotto.
    if (fr.readOnly === true) continue;
    fields.push({
      key,
      type: str(fr.type) || 'text',
      label: str(fr.label) || key,
      description: str(fr.description),
      required: fr.required === true || requiredKeys.has(key),
      placeholder: str(fr.placeholder),
      options: optionsOf(fr.options),
    });
  }

  const missingRequired = [...requiredKeys].filter(
    (k) => !LIMULO_PROFILE_SERVER_FIELDS.includes(k) && !resi.has(k),
  );

  return {
    fields,
    missingRequired,
    title: str(schema.title),
    description: str(schema.description),
    validationMode: str(provider.validationMode),
    authRequired: typeof provider.authRequired === 'boolean' ? provider.authRequired : null,
    gameName: str(game.name) || str(game.gameId),
    profileType,
  };
}

/**
 * I valori con cui il modulo PARTE: un campo richiesto con UNA sola opzione e' gia' scelto.
 *
 * Il caso vero e' la piattaforma del `generic`: le opzioni sono le piattaforme del gioco, e
 * con una sola chiederla sarebbe un tocco che non decide niente. Con due o piu' si sceglie.
 * I valori dell'utente vincono sempre: questi sono solo il punto di partenza.
 */
export function profileFormDefaults(fields: readonly LimuloProfileField[]): Record<string, string> {
  const defaults: Record<string, string> = {};
  for (const f of fields) {
    if (f.required && f.options.length === 1 && f.options[0].value !== '') {
      defaults[f.key] = f.options[0].value;
    }
  }
  return defaults;
}

// ─────────────────────────────────────────────────────────────────────────────
// 4. La validazione nel client — e la riga fra cio' che blocca e cio' che avvisa
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Il Player Tag di Clash Royale, normalizzato come lo normalizza il provider
 * (`app-game-clash-royale/backend/provider.js:54-60`): maiuscolo, senza spazi, un solo `#`.
 *
 * ⚠️ E' un DOPPIONE deliberato di codice altrui. Il motivo e' il fatto (1): un tag mal
 * formato non produce «Player Tag non valido» ma un `424` muto, perche' il `400` che
 * `createPlayerProfile` lancia con `validation.reason` (`runtime-services.js:2013-2015`)
 * viene appiattito da `resolveRuntimeCapability` prima di uscire. Il messaggio utile esiste
 * sul server e non arriva mai: l'unico modo di darlo a chi scrive e' averlo qui.
 */
export function normalizeClashTag(value: string): string {
  const normalized = value.trim().toUpperCase().replace(/\s+/g, '').replace(/^#+/, '');
  return normalized === '' ? '' : `#${normalized}`;
}

/** `provider.js:11`. Copia dichiarata, con la stessa data e la stessa fonte del normalizzatore. */
export const CLASH_TAG_PATTERN = /^#[0289PYLQGRJCUV]{3,16}$/;

/**
 * Le chiavi che sappiamo normalizzare/controllare. La conoscenza e' NOSTRA, non dello
 * schema: `profile-requirements` non porta ne' pattern ne' normalizzazione (i suoi campi
 * hanno `type`, `placeholder`, `description` e basta), quindi legarla alla chiave e'
 * l'unico aggancio disponibile. `clashRoyaleTag` e' l'alias che `PROVIDER_PAYLOAD_FIELDS`
 * accetta accanto a `playerTag` (`runtime-services.js:235-247`).
 */
const CHIAVI_TAG: readonly string[] = ['playerTag', 'clashRoyaleTag'];

/**
 * Il profilo di Tennis Clash: il TC ID (`externalPlayerId` dalla 0.1.3 del provider, QTennis@0c698f5)
 * si incolla a fiducia ed e' un UUID. Il modulo dei profili e' generico: questa e' l'unica regola
 * per gioco oltre a quella del tag CR.
 * ⚠️ Si aggancia al `profileType` DEL GIOCO, non alla chiave: `externalPlayerId` e' anche l'ID di
 * eFootball (`LIMULO_PROFILE_ID_DEDICATO`), che non e' un UUID e non deve ricevere l'avviso.
 */
export const LIMULO_PROFILE_TYPE_TENNIS_CLASH = 'tennis-clash';

/** UUID 8-4-4-4-12, maiuscole e minuscole (dalla copia dell'ID Giocatore di Tennis Clash). */
export const TC_ID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Vero se il campo e' il TC ID di un profilo Tennis Clash. */
export function isTennisClashId(profileType: string | null | undefined, key: string): boolean {
  return (
    (profileType ?? '').trim() === LIMULO_PROFILE_TYPE_TENNIS_CLASH && key === LIMULO_PROFILE_ID_DEDICATO
  );
}

export type LimuloFieldIssue = {
  key: string;
  /** `missing` = campo richiesto e vuoto. `shape` = la forma non torna. */
  kind: 'missing' | 'shape';
  /** La forma attesa, quando non e' quella del tag CR: dice quale frase mostrare. */
  expected?: 'uuid';
};

/**
 * I problemi del modulo, PRIMA di mandarlo.
 *
 * 🔴 **`missing` blocca, `shape` NO — e la distinzione e' la parte piu' importante di
 * questa funzione.**
 *
 * · `required` e' un **contratto**: lo schema lo dichiara, il server lo pretende, e mandare
 *   un campo richiesto vuoto e' un rifiuto certo e muto. Bloccare qui e' l'unica cosa utile.
 * · il pattern del tag e' un'**osservazione** dell'implementazione di un provider, letta il
 *   06/09. Bloccare su un'osservazione significa che il giorno in cui Supercell allargasse
 *   l'alfabeto — o il provider correggesse il suo regex — questa app rifiuterebbe da sola
 *   tag validi, **e nessuno lo scoprirebbe da qui**: non c'e' test che diventi rosso quando
 *   il mondo esterno cambia. Il costo delle due direzioni non e' simmetrico: un blocco
 *   sbagliato e' un vicolo cieco per l'utente, un passaggio sbagliato costa un rifiuto che
 *   questa sezione sa gia' spiegare.
 *   ⇒ si avvisa, con la forma attesa scritta accanto, e si lascia premere.
 */
export function profileFormIssues(
  fields: readonly LimuloProfileField[],
  values: Readonly<Record<string, string>>,
  profileType?: string | null,
): LimuloFieldIssue[] {
  const issues: LimuloFieldIssue[] = [];
  for (const f of fields) {
    const raw = values[f.key] ?? '';
    const value = CHIAVI_TAG.includes(f.key) ? normalizeClashTag(raw) : raw.trim();
    if (value === '') {
      if (f.required) issues.push({ key: f.key, kind: 'missing' });
      continue;
    }
    if (CHIAVI_TAG.includes(f.key) && !CLASH_TAG_PATTERN.test(value)) {
      issues.push({ key: f.key, kind: 'shape' });
    }
    // Il TC ID si controlla sul testo COSI' COME e' scritto: uno spazio attorno e' un avviso,
    // non un `trim` che lo aggiusta di nascosto.
    if (isTennisClashId(profileType, f.key) && !TC_ID_PATTERN.test(raw)) {
      issues.push({ key: f.key, kind: 'shape', expected: 'uuid' });
    }
  }
  return issues;
}

/** `true` se qualcosa impedisce di mandare. Solo i `missing`: v. la nota sopra. */
export function profileFormBlocking(issues: readonly LimuloFieldIssue[]): boolean {
  return issues.some((i) => i.kind === 'missing');
}

/**
 * Il corpo di `POST /v1/games/profiles`.
 *
 * ⚠️ `playerTag`/`apiToken` **non sono colonne del profilo**: `normalizePlayerPayload`
 * (`runtime-services.js:556-611`) ha una lista fissa di campi, e `normalizeProviderPayload`
 * (`:249-271`) instrada le chiavi di `PROVIDER_PAYLOAD_FIELDS` — fra cui `playerTag`,
 * `clashRoyaleTag`, `apiToken`, `token` — dentro `meta.provider`. Mandarli al livello alto
 * del corpo e' quindi la cosa giusta, non una scorciatoia: e' il posto da cui il provider
 * li raccoglie.
 *
 * ⛔ **Mai `userId`**: lo mette il server (v. `LIMULO_PROFILE_SERVER_FIELDS`).
 * I campi vuoti non si mandano: una stringa vuota e un campo assente non sono la stessa
 * cosa per `normalizePlayerPayload`, che su `undefined` ripiega sull'esistente e su `''` no.
 */
/**
 * Il motivo da mostrare quando una scrittura del profilo e' rifiutata.
 *
 * 🔴 Sul 400/422 il motivo del provider arriva nel corpo, campo `error` (il BFF lo rende pubblico:
 * `public-errors.js`, lettura di limulo-core-orc 2026-09-23). Il Relink di Luca su stg-tma diceva
 * solo il titolo del verdetto, e il motivo — token non valido per il tag, o altro — restava nel
 * log del BFF, dove nessun giocatore guarda. Per gli altri verdetti il messaggio non e' un motivo
 * dell'utente (guasti, rete, ruoli): resta il titolo.
 */
export function motivoDelRifiuto(failure: Pick<LimuloFailure, 'verdict' | 'message'>, titolo: string): string {
  const datiRifiutati = failure.verdict === 'bad-request' || failure.verdict === 'unprocessable';
  const messaggio = typeof failure.message === 'string' ? failure.message.trim() : '';
  return datiRifiutati && messaggio !== '' ? `${titolo}: ${messaggio}` : titolo;
}

/**
 * Il corpo del RICOLLEGA (PATCH): quello della creazione, piu' `externalPlayerId` = il tag.
 *
 * 🔴 [M limulo-core-orc 2026-09-23 18:55Z, `qlash-limulo@origin/staging`] Sul PATCH
 * `normalizePlayerPayload` (app-games) tiene `externalPlayerId: payload.externalPlayerId ??
 * existing.externalPlayerId`, e il provider CR legge il tag come
 * `profile.playerTag || profile.clashRoyaleTag || profile.externalPlayerId` sul profilo
 * NORMALIZZATO, dove `playerTag` non c'e' (sta solo in `meta.provider`). ⇒ Con il solo
 * `playerTag` nel corpo vince il tag VECCHIO del profilo, e il token nuovo si verifica contro
 * quello: 400 «token non valido per questo Player Tag». Il Relink di Luca su stg-tma e' stato
 * rifiutato cosi' sei volte (17:14–18:53Z), bruciando un token a ogni tentativo.
 * ✅ La forma misurata che funziona (I-20, 12/09) e' `{externalPlayerId, apiToken}`.
 * Alla CREAZIONE non serve: non c'e' un profilo esistente da cui ereditare.
 */
export function relinkBody(body: Readonly<Record<string, string>>): Record<string, string> {
  const tag = body.playerTag || body.clashRoyaleTag || '';
  return tag === '' ? { ...body } : { ...body, externalPlayerId: tag };
}

export function profileFormBody(
  gameId: string,
  fields: readonly LimuloProfileField[],
  values: Readonly<Record<string, string>>,
): Record<string, string> {
  const body: Record<string, string> = { gameId };
  for (const f of fields) {
    const raw = values[f.key] ?? '';
    const value = CHIAVI_TAG.includes(f.key) ? normalizeClashTag(raw) : raw.trim();
    if (value !== '') body[f.key] = value;
  }
  return body;
}

// ─────────────────────────────────────────────────────────────────────────────
// 5. La creazione del profilo, con la stessa rilettura
// ─────────────────────────────────────────────────────────────────────────────

export type LimuloProfileCreateOutcome =
  | 'created'
  /** La POST ha rifiutato, ma rileggendo un profilo per QUESTO gioco c'e'. */
  | 'already-there'
  /** La POST ha rifiutato e rileggendo non c'e' nessun profilo per questo gioco. */
  | 'refused'
  /** La POST ha rifiutato e la rilettura non e' riuscita. */
  | 'refused-unknown';

export interface LimuloProfileCreateReport {
  outcome: LimuloProfileCreateOutcome;
  failure: LimuloFailure | null;
  /**
   * 🔴 `profileValidation.status`, e va mostrato invece che tradotto in «verificato».
   *
   * `unverified_allowed` significa **il contrario** di verificato: scatta solo quando la
   * verifica non e' stata fatta perche' l'organizzazione non ha una chiave Supercell
   * server-side (`verification.status === 'not_configured'`) e le impostazioni ammettono
   * profili non verificati (`provider.js:838-843`, `:217-227` a `qlash-limulo@origin/staging`
   * `fd57be9`; le righe 806-811 citate prima sono diventate il ramo «gia verificato»). E' esattamente lo stato
   * misurato su staging il 06/09. Scrivere «profilo verificato» li' sarebbe una frase
   * falsa detta con la faccia di una conferma.
   */
  validationStatus: string | null;
  profileId: string | null;
}

const validationStatusOf = (profile: LimuloProfile | null): string | null => {
  if (profile === null) return null;
  const v = record(profile.profileValidation);
  const status = str(v.status);
  return status === '' ? null : status;
};

/**
 * Il profilo, creato e riletto — stessa mossa dell'iscrizione, per la stessa ragione.
 *
 * ⚠️ La rilettura confronta `p.gameId === gameId` con la stessa stringa che abbiamo
 * mandato: `normalizePlayerPayload` la conserva tale e quale
 * (`gameId: toSafeString(payload.gameId ?? existing.gameId).trim()`), quindi il confronto
 * e' fra due valori dello stesso spazio. Se un giorno non lo fosse, l'errore cadrebbe dalla
 * parte prudente — «non lo trovo» invece di un successo affermato a vuoto.
 */
export async function createProfileReconciled(input: {
  createProfile: (body: Record<string, string>) => Promise<LimuloCallOutcome<LimuloProfile | null>>;
  /** `GET /v1/games/profiles` — la RILETTURA. */
  readProfiles: () => Promise<LimuloCallOutcome<LimuloProfile[]>>;
  gameId: string;
  body: Record<string, string>;
}): Promise<LimuloProfileCreateReport> {
  const esito = await input.createProfile(input.body);
  const errore = outcomeError(esito);

  if (errore === undefined) {
    const creato = outcomeData(esito) ?? null;
    return {
      outcome: 'created',
      failure: null,
      validationStatus: validationStatusOf(creato),
      profileId: idDelProfilo(creato) || null,
    };
  }

  const failure = limuloFailure(errore);
  const riletto = await input.readProfiles();
  const lista = outcomeData(riletto);
  if (lista === undefined || !Array.isArray(lista)) {
    return { outcome: 'refused-unknown', failure, validationStatus: null, profileId: null };
  }
  const trovato = lista.find((p) => p.gameId === input.gameId) ?? null;
  if (trovato === null) {
    return { outcome: 'refused', failure, validationStatus: null, profileId: null };
  }
  return {
    outcome: 'already-there',
    failure,
    validationStatus: validationStatusOf(trovato),
    profileId: idDelProfilo(trovato) || null,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// 6. Il profilo IMPLICITO — quando lo schema del gioco non chiede niente (orc41)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Che cosa fare del profilo di gioco quando l'iscrizione ne pretende uno e l'utente non ce l'ha.
 *
 * 🔴 IL REPERTO (qgammon-orc, 2026-09-24 21:37Z, GO di Luca): su un torneo backgammon di collaudo
 * su `stg-tma` Luca e Martha, a «Iscriviti», leggono «You need a game profile for this game before
 * you can join» — e la CTA porta a un modulo che per il backgammon non ha NIENTE da chiedere: lo
 * schema del provider rende zero campi, il profilo e' l'identita' Logto e il `sync` ci scrive il
 * `sub` (QGammon `limulo-app/README.md`, «Il profilo è l'identità»). Il profilo pero' deve
 * ESISTERE: `external-match.create` lo usa per dire a QGammon chi gioca.
 *
 * · `non-serve`   — c'e' gia' un profilo per il gioco, o la lista non ha risposto (ci pensa
 *                   `attesaProfili`), o il torneo non ha un gioco: il percorso di oggi;
 * · `in-lettura`  — serve, e lo schema del provider e' in volo: «Iscriviti» aspetta;
 * · `illeggibile` — serve, e lo schema e' in errore senza una risposta precedente: aspetta, e si
 *                   offre di riprovare;
 * · `modulo`      — serve, e lo schema chiede qualcosa all'utente (Clash Royale: tag e token), o
 *                   la risposta non descrive uno schema: il percorso di oggi, CTA «crea profilo»;
 * · `implicito`   — serve, e lo schema ha ZERO campi da rendere: «Iscriviti» crea il profilo e
 *                   poi iscrive (`iscriviConProfiloImplicito`), senza mandare a un modulo vuoto.
 *
 * 🔑 **IL CRITERIO E' LO SCHEMA DEL PROVIDER, MAI IL NOME DEL GIOCO.** Si legge con
 * `profileFormPlan`, cioe' con gli stessi campi che la schermata di creazione renderebbe
 * (`LIMULO_PROFILE_SERVER_FIELDS` e i `readOnly` esclusi): se li' non ci sarebbe niente da
 * compilare, qui non c'e' niente da chiedere. Il backgammon e' questo caso oggi
 * (`profileSchemaRead`: `fields: []`, `required: ['userId','gameId']`, QGammon
 * `limulo-app/backend/provider.js`); un gioco nuovo con lo stesso schema ci cade da solo, e un
 * backgammon che un giorno chiedesse un campo ne esce da solo.
 *
 * ⚠️ «Zero campi» vuole uno schema che li DICHIARA: `schema.fields` dev'essere un array. Una
 * risposta senza `schema` non e' «niente da compilare», e' una risposta che non descrive un modulo
 * — la distinzione che il gate tiene su `profileFormPlan({})` e che `LimuloProfileCreate` scrive a
 * schermo (`profileCreate.noFields`). Quella va su `modulo`: il percorso di oggi, che non crea.
 * ⚠️ Una chiave richiesta senza campo (`missingRequired`) toglie l'implicito: la creazione
 * prenderebbe un rifiuto certo, e il modulo sa gia' dirlo prima (`profileCreate.incomplete`).
 * ⛔ Schema in volo o in errore ⇒ MAI `implicito`: non si crea niente su una risposta che non c'e'.
 * L'errore conta solo SENZA dati, come in `attesaProfili`: uno schema gia' arrivato e una rilettura
 * fallita lasciano in mano la risposta del server. `undefined` = non arrivato; `null` e' un 2xx
 * senza `data` (`unwrapData`), cioe' una risposta — e non descrive uno schema.
 */
export type PianoProfiloImplicito = 'non-serve' | 'in-lettura' | 'illeggibile' | 'modulo' | 'implicito';

export function pianoProfiloImplicito(input: {
  scelta: ProfiloPerIscrizione;
  gameId: string;
  /** La risposta di `GET /v1/games/:gameId/profile-requirements`; `undefined` finche' non arriva. */
  requisitiGioco: unknown;
  inErrore: boolean;
}): PianoProfiloImplicito {
  if (input.gameId.trim() === '' || input.scelta.kind !== 'nessuno') return 'non-serve';
  if (input.requisitiGioco === undefined) return input.inErrore ? 'illeggibile' : 'in-lettura';
  return schemaAZeroCampi(input.requisitiGioco) ? 'implicito' : 'modulo';
}

/**
 * Lo schema del provider dichiara ZERO campi da chiedere all'utente? E' il criterio di
 * `pianoProfiloImplicito`, estratto perche' lo usa anche il SYNC (orc44, v. `sincronizzaProfilo`):
 * «si crea senza modulo» e «il profilo va sincronizzato» devono dire di si' agli STESSI giochi —
 * sono le due meta' dello stesso disegno («il profilo e' l'identita'»), e due regole scritte in due
 * posti divergerebbero al primo gioco nuovo.
 * ⚠️ `undefined` (non arrivato) e `null` (2xx senza `data`) ⇒ `false`: non descrivono uno schema.
 */
export function schemaAZeroCampi(requisitiGioco: unknown): boolean {
  if (!Array.isArray(record(record(requisitiGioco).schema).fields)) return false;
  const piano = profileFormPlan(requisitiGioco);
  return piano.fields.length === 0 && piano.missingRequired.length === 0;
}

/**
 * Il profilo da sincronizzare DOPO una creazione dal modulo «Collega» (`LimuloProfileCreate`), o `null` (I-349):
 * «il profilo si crea automaticamente» deve valere anche fuori dai tornei, cioe' un profilo a ZERO campi appena
 * creato (o gia' c'era: `already-there`) nasce col sync, come quello dell'iscrizione. Con campi (Clash Royale: tag e
 * token) niente sync: li' `meta.provider` lo scrive la validazione col token. Una creazione rifiutata, o una
 * risposta senza `profileId`, non sincronizza niente.
 */
export function profiloDaSincronizzareDopoCreazione(input: {
  report: Pick<LimuloProfileCreateReport, 'outcome' | 'profileId'>;
  requisitiGioco: unknown;
}): string | null {
  if (input.report.outcome !== 'created' && input.report.outcome !== 'already-there') return null;
  if (!schemaAZeroCampi(input.requisitiGioco)) return null;
  const id = input.report.profileId?.trim() ?? '';
  return id === '' ? null : id;
}

/**
 * Cosa deve succedere PRIMA della join quando si preme «Entra» in un sit&go (I-349): il profilo e' l'identita'
 * del giocatore per quel gioco, e a un gioco a ZERO campi (il backgammon) il profilo serve ESISTERE e AVERE il
 * `sub` che solo il sync scrive — senza, la join prende `422 queue.profile-rejected`.
 *
 *  · `subito`        — niente da preparare: la join parte con quello che c'e' (profilo gia' scelto in un gioco con
 *                      campi — Clash Royale —, gioco senza id, lista dei profili non ancora arrivata, schema che
 *                      chiede un modulo: il rifiuto e il «Collega» della join restano quelli di sempre);
 *  · `attendi`       — NESSUN profilo e lo schema non e' ancora arrivato: serve a decidere crea o modulo, «Entra»
 *                      aspetta (una GET sola, in cache) e la riga lo dice;
 *  · `verifica-schema` — c'e' un profilo e lo schema non e' ancora arrivato: «Entra» resta ACCESO e NON compare
 *                      nessuna riga (per Clash Royale sarebbe un lampo a ogni prima apertura). Al tap lo schema si
 *                      aspetta (la lettura in volo) e poi si decide: zero campi ⇒ sync atteso e join; con campi ⇒ join;
 *  · `illeggibile`   — NESSUN profilo e lo schema non si legge: aspetta e si offre di riprovare (non si crea niente
 *                      su una risposta che non c'e'). ⚠️ Con un profilo GIA' scelto uno schema illeggibile NON ferma:
 *                      Clash Royale entra come prima, e il backgammon prende il motivo della join;
 *  · `crea-profilo`  — nessun profilo e lo schema ha zero campi: «Entra» crea il profilo, lo sincronizza, poi entra;
 *  · `sincronizza`   — c'e' un profilo e lo schema ha zero campi: «Entra» ASPETTA il sync e poi entra.
 * 🔑 Il criterio e' lo schema del provider, mai il nome del gioco (come `pianoProfiloImplicito`).
 */
export type PianoIngressoCoda =
  | 'subito'
  | 'attendi'
  | 'verifica-schema'
  | 'illeggibile'
  | 'crea-profilo'
  | 'sincronizza';

export function pianoIngressoCoda(input: {
  scelta: ProfiloPerIscrizione;
  gameId: string;
  /** La risposta di `GET /v1/games/:gameId/profile-requirements`; `undefined` finche' non arriva. */
  requisitiGioco: unknown;
  inErrore: boolean;
}): PianoIngressoCoda {
  if (input.gameId.trim() === '' || input.scelta.kind === 'sconosciuto') return 'subito';
  if (input.scelta.kind === 'nessuno') {
    const piano = pianoProfiloImplicito(input);
    if (piano === 'implicito') return 'crea-profilo';
    if (piano === 'in-lettura') return 'attendi';
    if (piano === 'illeggibile') return 'illeggibile';
    return 'subito';
  }
  if (input.requisitiGioco === undefined) return input.inErrore ? 'subito' : 'verifica-schema';
  return schemaAZeroCampi(input.requisitiGioco) ? 'sincronizza' : 'subito';
}

// ═════════════════════════════════════════════════════════════════════════════
// IL SYNC DEL PROFILO A ZERO CAMPI (orc44)
// ═════════════════════════════════════════════════════════════════════════════

/**
 * 🔴 IL REPERTO (qgammon-orc 2026-09-24 23:29Z, GO di Luca «A»): torneo backgammon, la partita
 * esterna nasce in `conflict` per `sub_missing` su entrambi i giocatori. I loro profili li ha
 * creati la TMA col profilo implicito (#377): hanno `meta.provider` SENZA `sub`, perche' il `sub`
 * lo scrive solo `game.profile-provider.sync` (ricavandolo dall'attore), e nessuno lo chiamava.
 *
 * La cura ha DUE sedi, e si chiamano da qui:
 *  1. subito dopo la creazione implicita, prima dell'iscrizione (`iscriviConProfiloImplicito`);
 *  2. all'apertura di una scheda torneo o incontro, per chi ha GIA' un profilo attivo a zero campi
 *     — i profili nati prima di questa cura (`syncAllApertura`, montata da `useSyncProfiloLimulo`).
 *
 * ⚠️ **Il sync non blocca mai niente.** E' una riparazione del provider, non un requisito
 * dell'iscrizione: un rifiuto o un'eccezione finiscono nel registro (`log`), e si prosegue. Un
 * sync che non risponde e' tagliato a `SYNC_TETTO_MS`: «Iscriviti» non puo' restare appeso a una
 * chiamata accessoria.
 * ⚠️ **Una volta per profilo e per sessione**: il Set e' in memoria, di modulo. Non a ogni render
 * e non a ogni giro di polling — il sync invalida `LimuloProfile`, e senza il Set la rilettura
 * che provoca ne farebbe partire un altro, all'infinito. Si segna PRIMA della chiamata: un sync
 * fallito non si ripete a ogni render (la prossima sessione ci riprova).
 */
const PROFILI_SINCRONIZZATI = new Set<string>();

export const SYNC_TETTO_MS = 8000;

export type LimuloProfileSync = (profileId: string) => Promise<LimuloCallOutcome<unknown>>;

/**
 * I sync IN VOLO, per profilo: chi deve ASPETTARE un sync (l'«Entra» di un sit&go, `attendiSyncProfilo`) si
 * aggancia alla promessa di chi l'ha gia' lanciato (la sede 2 all'apertura di una scheda, un «Collega»
 * appena finito) invece di scoprire che l'id e' nel Set e andare avanti: il Set dice «gia' chiesto», non
 * «gia' finito». Una promessa per profilo, tolta quando si chiude; non rifiuta mai (`eseguiSyncNudo` non lancia).
 */
const SYNC_IN_VOLO = new Map<string, Promise<void>>();

/** Una chiamata di sync, registrata fra quelle in volo. Non lancia e non resta appesa: l'esito va solo nel registro. */
function eseguiSync(
  profileId: string,
  sync: LimuloProfileSync,
  log: (msg: string) => void,
  inVolo: Map<string, Promise<void>> = SYNC_IN_VOLO,
): Promise<void> {
  const promessa = eseguiSyncNudo(profileId, sync, log);
  inVolo.set(profileId, promessa);
  void promessa.finally(() => {
    if (inVolo.get(profileId) === promessa) inVolo.delete(profileId);
  });
  return promessa;
}

/**
 * **L'ultimo esito del sync, per profilo** (kb#388, tma-orcr-43): fino a qui l'esito andava solo nel registro `dbg` e
 * la Sfida non lo vedeva mai. Tre esiti, e la differenza e' cio' che l'utente puo' farci:
 *  · `ok`         — il server ha risposto bene;
 *  · `rifiutato`  — il server ha RISPOSTO con un errore HTTP (`status`; il `code` solo se il BFF lo manda). Un 403 e'
 *                   «identita' assente o ambigua» del game host: ritentare non serve, il rimedio non e' dell'utente;
 *  · `guasto`     — nessuna risposta: tetto di `SYNC_TETTO_MS`, rete caduta, eccezione. Ritentare serve.
 * ⚠️ Il `code` `chess.sub.identita-*` del game host NON arriva al client oggi: il BFF promuove un 4xx del provider a
 * `E_QLASH_HTTP_<status>` e passa solo i `details` in lista bianca (`router.js:267-281` e `public-errors.js:150-197`,
 * Limulo origin/staging, letti per ref il 2026-10-09). Per questo il 403 si riconosce dallo STATUS, e il code e' un di piu'.
 * ⚠️ Un esito per profilo, di modulo, riscritto a ogni sync: vale per la sessione, come il Set `PROFILI_SINCRONIZZATI`.
 */
export type EsitoSyncProfilo =
  | { kind: 'ok' }
  | { kind: 'rifiutato'; status: number; code: string | null }
  | { kind: 'guasto' };

const ESITI_SYNC = new Map<string, EsitoSyncProfilo>();
/**
 * Il numero d'ordine dell'ULTIMO sync lanciato per profilo: un sync che risponde DOPO il tetto scrive il suo esito solo se nel
 * frattempo non ne e' partito uno piu' nuovo (altrimenti una risposta vecchia, arrivata tardi, cancellerebbe un esito piu' fresco).
 */
const ORDINE_SYNC = new Map<string, number>();

/** L'ultimo esito noto del sync di un profilo, o `undefined` se in questa sessione non e' mai finito un sync. */
export function esitoSyncDi(profileId: string): EsitoSyncProfilo | undefined {
  return ESITI_SYNC.get(profileId.trim());
}

/** Solo per i test: il registro e' di modulo e dura quanto la sessione del file. */
export function azzeraEsitiSyncPerTest(): void {
  ESITI_SYNC.clear();
  ORDINE_SYNC.clear();
}

/** Dall'esito di una chiamata riuscita (nessuna eccezione): risposta buona, errore HTTP, o errore senza status (rete, timeout). */
function esitoDellaRisposta(esito: LimuloCallOutcome<unknown>): { esito: EsitoSyncProfilo; f?: LimuloFailure } {
  const errore = outcomeError(esito);
  if (errore === undefined) return { esito: { kind: 'ok' } };
  const f = limuloFailure(errore);
  return { esito: f.status === null ? { kind: 'guasto' } : { kind: 'rifiutato', status: f.status, code: f.code }, f };
}

/**
 * Una chiamata di sync, che non lancia e non resta appesa: l'esito va nel registro `dbg` e in `ESITI_SYNC`.
 * ⚠️ Passato il tetto la chiamata NON si butta: se risponde dopo, il suo esito (ok o rifiuto) sostituisce il `guasto` del tetto —
 * ma solo se nel frattempo non e' partito un sync piu' nuovo per lo stesso profilo (`ORDINE_SYNC`). Senza, un sync riuscito a 9 s
 * lasciava `guasto` e il tocco dopo riaspettava fino a 8 s per niente.
 */
async function eseguiSyncNudo(profileId: string, sync: LimuloProfileSync, log: (msg: string) => void): Promise<void> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const ordine = (ORDINE_SYNC.get(profileId) ?? 0) + 1;
  ORDINE_SYNC.set(profileId, ordine);
  const scrivi = (esito: EsitoSyncProfilo) => {
    if (ORDINE_SYNC.get(profileId) === ordine) ESITI_SYNC.set(profileId, esito);
  };
  try {
    const tetto = new Promise<'tetto'>((resolve) => {
      timer = setTimeout(() => resolve('tetto'), SYNC_TETTO_MS);
    });
    const chiamata = Promise.resolve(sync(profileId));
    const esito = await Promise.race([chiamata, tetto]);
    if (esito === 'tetto') {
      scrivi({ kind: 'guasto' });
      log(`[limulo] sync profilo ${profileId}: nessuna risposta in ${SYNC_TETTO_MS}ms, si prosegue`);
      // La risposta tardiva, se arriva, corregge il registro (e solo quello: nessuno la aspetta piu').
      void chiamata.then(
        (tardiva) => {
          const letta = esitoDellaRisposta(tardiva);
          scrivi(letta.esito);
          log(`[limulo] sync profilo ${profileId}: risposta dopo il tetto (${letta.esito.kind})`);
        },
        () => undefined,
      );
      return;
    }
    const letta = esitoDellaRisposta(esito);
    scrivi(letta.esito);
    if (letta.f !== undefined) log(`[limulo] sync profilo ${profileId} rifiutato: ${letta.f.status ?? '—'} ${letta.f.verdict}`);
  } catch (e) {
    scrivi({ kind: 'guasto' });
    log(`[limulo] sync profilo ${profileId} in errore: ${e instanceof Error ? e.message : String(e)}`);
  } finally {
    if (timer !== undefined) clearTimeout(timer);
  }
}

/**
 * Il sync di UN profilo, una volta per sessione. `true` se e' partito ora, `false` se era gia'
 * stato chiesto (o l'id e' vuoto). `giaFatti` si inietta nei test; in produzione e' il Set di modulo.
 */
export async function sincronizzaProfilo(input: {
  profileId: string;
  sync: LimuloProfileSync;
  log: (msg: string) => void;
  giaFatti?: Set<string>;
  /** Solo per i test: in produzione e' la mappa di modulo. */
  inVolo?: Map<string, Promise<void>>;
}): Promise<boolean> {
  const fatti = input.giaFatti ?? PROFILI_SINCRONIZZATI;
  const id = input.profileId.trim();
  if (id === '' || fatti.has(id)) return false;
  fatti.add(id);
  await eseguiSync(id, input.sync, input.log, input.inVolo);
  return true;
}

/**
 * **Il sync che si ASPETTA** — per chi ha un passo che dipende dal `sub` appena scritto (l'«Entra» di un sit&go:
 * la validazione di QGammon vuole `meta.provider.sub`, e senza il sync la join prende `queue.profile-rejected`).
 * Tre casi, e nessuno batte il sync:
 *  · un sync per quel profilo e' IN VOLO (lo ha lanciato la sede 2 o il «Collega», senza aspettarlo): ci si aggancia;
 *  · e' gia' stato chiesto e finito in questa sessione (`giaFatti`, niente in volo): non si ripete, si prosegue;
 *  · mai chiesto: si chiede ADESSO e si aspetta (`sincronizzaProfilo`).
 * ⚠️ Non lancia e non resta appeso piu' di `SYNC_TETTO_MS`, come ogni sync: un rifiuto va nel registro e chi
 * chiama prosegue — se il `sub` manca davvero, e' la join a dirlo, col suo motivo.
 * ⚠️ Non tocca `sincronizzaProfilo` ne' le due sedi di prima: il Set e la loro semantica restano quelli.
 */
export async function attendiSyncProfilo(input: {
  profileId: string;
  sync: LimuloProfileSync;
  log: (msg: string) => void;
  giaFatti?: Set<string>;
  inVolo?: Map<string, Promise<void>>;
}): Promise<void> {
  const id = input.profileId.trim();
  if (id === '') return;
  const inVolo = input.inVolo ?? SYNC_IN_VOLO;
  const fatti = input.giaFatti ?? PROFILI_SINCRONIZZATI;
  const inCorso = inVolo.get(id);
  if (inCorso !== undefined) {
    await inCorso;
    return;
  }
  if (fatti.has(id)) return;
  await sincronizzaProfilo({ profileId: id, sync: input.sync, log: input.log, giaFatti: fatti, inVolo });
}

/**
 * **Cosa trova il TOCCO di Invia o Accetta quando il profilo e' gia' li'** (kb#388, tma-orcr-43). Il sync lo ha lanciato
 * la scheda o la riga all'apertura, senza aspettarlo, e il tocco puo' arrivare prima che atterri (⇒ `sub_missing` a
 * una sfida che un secondo dopo sarebbe passata) o dopo che e' caduto (⇒ il Set impedisce di rifarlo, e ogni tocco
 * rifa' lo stesso rifiuto). Tre esiti, e solo `ok` lascia partire la sfida:
 *  · `ok`           — niente da aspettare, o il sync e' andato: si manda;
 *  · `identita`     — il server ha risposto 403: NIENTE ritentativo e niente sfida, il rimedio non e' dell'utente;
 *  · `non-risponde` — il sync (atteso ora, UNO solo per tocco) e' caduto di nuovo: niente sfida, «riprova fra qualche minuto».
 */
export type EsitoSyncAlTocco =
  | { kind: 'ok' }
  | { kind: 'identita'; status: number; code: string | null }
  | { kind: 'non-risponde' };

/** 501 «non implementato»: una risposta di configurazione come un 4xx, non un guasto (decisione di tma-orcr, revisione di #678). */
const STATUS_SYNC_NON_SERVITO = 501;

/**
 * Dall'esito registrato al verdetto del tocco — UNA funzione sola, che leggono sia il tocco sia la frase del rifiuto del server.
 *  · senza esito (mai finito un sync) o `ok`  ⇒ `ok`: si manda;
 *  · 403                                       ⇒ `identita`: il game host non ha l'identita', ritentare non serve;
 *  · un ALTRO 4xx (404/422/424/…) o un 501    ⇒ `ok`: e' un verdetto di CONFIGURAZIONE (rotta o gioco non serviti), non un guasto, e
 *    «riprova fra qualche minuto» non lo cambia. Il tocco procede come prima di kb#388 (sync best-effort, la Sfida parte) e decide
 *    la risposta del server;
 *  · rete, timeout, eccezione, 5xx            ⇒ `non-risponde`: e' un guasto, ritentare serve.
 */
export function verdettoDelSync(esito: EsitoSyncProfilo | undefined): EsitoSyncAlTocco {
  if (esito === undefined || esito.kind === 'ok') return { kind: 'ok' };
  if (esito.kind === 'rifiutato') {
    if (esito.status === STATUS_SYNC_IDENTITA) return { kind: 'identita', status: esito.status, code: esito.code };
    if ((esito.status >= 400 && esito.status < 500) || esito.status === STATUS_SYNC_NON_SERVITO) return { kind: 'ok' };
  }
  return { kind: 'non-risponde' };
}

/** L'esito GIA' registrato per un profilo, letto senza ritentare: serve dopo un «crea e sincronizza» che ha appena atteso il suo sync. */
export function esitoSyncRegistrato(profileId: string | null): EsitoSyncAlTocco {
  const id = (profileId ?? '').trim();
  return id === '' ? { kind: 'ok' } : verdettoDelSync(ESITI_SYNC.get(id));
}

/**
 * Meta' SINCRONA del tocco: dice se si puo' proseguire subito (come prima di kb#388, senza una microtask in mezzo)
 * o se bisogna aspettare un sync. `'attendi'` ⇔ un sync per quel profilo e' IN VOLO, oppure l'ultimo e' caduto per
 * un guasto e va rifatto. Un 403 registrato NON si ritenta: e' gia' un verdetto.
 */
export function giudizioSyncAlTocco(
  profileId: string | null,
  inVolo: Map<string, Promise<void>> = SYNC_IN_VOLO,
): 'attendi' | EsitoSyncAlTocco {
  const id = (profileId ?? '').trim();
  if (id === '') return { kind: 'ok' };
  if (inVolo.has(id)) return 'attendi';
  const verdetto = verdettoDelSync(ESITI_SYNC.get(id));
  return verdetto.kind === 'non-risponde' ? 'attendi' : verdetto;
}

/**
 * L'altra meta': aspetta il sync del tocco. In volo ⇒ ci si aggancia; caduto ⇒ si SEGNA di nuovo il profilo e si rifa'
 * UNA volta, aspettandolo (tetto `SYNC_TETTO_MS`). Non lancia. Se anche quello cade il verdetto e' `non-risponde`:
 * niente giro infinito, il tocco dopo ne rifara' un altro.
 */
export async function attendiSyncAlTocco(input: {
  profileId: string | null;
  sync: LimuloProfileSync;
  log: (msg: string) => void;
  giaFatti?: Set<string>;
  inVolo?: Map<string, Promise<void>>;
}): Promise<EsitoSyncAlTocco> {
  const id = (input.profileId ?? '').trim();
  if (id === '') return { kind: 'ok' };
  const inVolo = input.inVolo ?? SYNC_IN_VOLO;
  const fatti = input.giaFatti ?? PROFILI_SINCRONIZZATI;
  const inCorso = inVolo.get(id);
  if (inCorso !== undefined) {
    await inCorso;
  } else {
    fatti.delete(id);
    await sincronizzaProfilo({ profileId: id, sync: input.sync, log: input.log, giaFatti: fatti, inVolo });
  }
  return esitoSyncRegistrato(id);
}

/**
 * Sede 2: all'apertura della scheda (torneo o incontro). Parte un sync per ogni profilo ATTIVO
 * dell'utente per quel gioco — la lista e' quella di `GET /v1/games/profiles`, che per default da'
 * gli attivi — SOLO se lo schema del gioco e' a zero campi. Clash Royale (tag e token) non lo
 * chiama mai: li' `meta.provider` lo scrive la validazione col token, non il sync.
 * Restituisce gli id per cui il sync e' partito ADESSO (fire-and-forget: non si aspetta l'esito).
 */
export function syncAllApertura(input: {
  profili: readonly LimuloProfile[] | null | undefined;
  gameId: string;
  requisitiGioco: unknown;
  sync: LimuloProfileSync;
  log: (msg: string) => void;
  giaFatti?: Set<string>;
}): string[] {
  const fatti = input.giaFatti ?? PROFILI_SINCRONIZZATI;
  if (input.gameId.trim() === '' || !Array.isArray(input.profili)) return [];
  if (!schemaAZeroCampi(input.requisitiGioco)) return [];
  const ids = input.profili
    .filter((p) => p?.gameId === input.gameId)
    .map((p) => idDelProfilo(p))
    .filter((id) => id !== '' && !fatti.has(id));
  const partiti = [...new Set(ids)];
  for (const id of partiti) {
    void sincronizzaProfilo({ profileId: id, sync: input.sync, log: input.log, giaFatti: fatti });
  }
  return partiti;
}

/**
 * L'esito di «Iscriviti» sul percorso IMPLICITO: prima il profilo, poi l'iscrizione.
 *
 * · `profilo`          — la creazione e' fallita (`refused` / `refused-unknown`): NESSUNA
 *                        iscrizione. Mandarla lo stesso prenderebbe il `profile.required` del
 *                        reperto, con un passo in piu' e lo stesso messaggio falso.
 * · `profilo-senza-id` — il profilo risulta creato (o c'era gia'), ma la risposta non porta un
 *                        identificativo: nessuna iscrizione, perche' senza `profileId` la POST
 *                        partirebbe nuda. Il profilo pero' c'e': la creazione invalida la lista
 *                        (`invalidatesTags: ['LimuloProfile']`) e il tap successivo lo trova li'.
 * · `iscrizione`       — il profilo c'e' e l'iscrizione e' partita col SUO id: l'esito e' quello di
 *                        `registerReconciled`, identico al percorso di oggi. Se l'iscrizione e'
 *                        rifiutata il profilo RESTA, ed e' giusto: e' l'identita' del giocatore
 *                        per quel gioco, non un accessorio di questo torneo.
 */
export type LimuloImplicitRegistrationReport =
  | { fase: 'profilo'; profilo: LimuloProfileCreateReport }
  | { fase: 'profilo-senza-id'; profilo: LimuloProfileCreateReport }
  | {
      fase: 'iscrizione';
      /** `null` = la creazione non e' partita (manca l'identita': v. sotto). */
      profilo: LimuloProfileCreateReport | null;
      iscrizione: LimuloRegistrationReport;
    };

/**
 * Crea il profilo (con la rilettura di `createProfileReconciled`), poi iscrive con l'id APPENA
 * ottenuto.
 *
 * 🔴 **Il `profileId` dell'iscrizione e' quello della creazione, passato qui dentro da una chiamata
 * all'altra** — mai riletto dalla lista del pannello. Quella lista, nel render in cui «Iscriviti» e'
 * stato premuto, dice «nessuno» per costruzione: rileggerla da una chiusura sarebbe la corsa di
 * orc39 (#375) a parti invertite, con la POST che parte nuda subito dopo aver creato il profilo.
 *
 * ⚠️ Prima l'IDENTITA', poi la creazione: senza l'id di `/v1/me` l'iscrizione non parte comunque
 * (`identity-missing`), e creare un profilo per un'iscrizione che non partira' sarebbe una
 * scrittura senza scopo, fatta per conto dell'utente.
 * ⚠️ `already-there` (la POST rifiutata, ma rileggendo il profilo per questo gioco c'e') non e' una
 * creazione fallita: il profilo esiste, e si iscrive con quello.
 */
export async function iscriviConProfiloImplicito(input: {
  createProfile: (body: Record<string, string>) => Promise<LimuloCallOutcome<LimuloProfile | null>>;
  /** `GET /v1/games/profiles` — la rilettura dopo un rifiuto della creazione. */
  readProfiles: () => Promise<LimuloCallOutcome<LimuloProfile[]>>;
  register: (body: LimuloRegistrationBody) => Promise<LimuloCallOutcome<LimuloRegistrationResult | null>>;
  readRegistration: () => Promise<LimuloCallOutcome<LimuloRegistrationStatus | null>>;
  /** `POST /v1/games/profiles/:profileId/sync` — orc44, v. `sincronizzaProfilo`. */
  syncProfile: LimuloProfileSync;
  /** Il registro di debug (`dbg`): un sync fallito si scrive li', e non tocca l'iscrizione. */
  log: (msg: string) => void;
  /** Solo per i test: in produzione e' il Set di modulo. */
  giaSincronizzati?: Set<string>;
  memberId: string;
  gameId: string;
  /** Il consenso al regolamento, passato tale e quale alla POST che segue il profilo (v. `registerReconciled`). */
  acceptedRulesVersion?: number | null;
}): Promise<LimuloImplicitRegistrationReport> {
  const iscrivi = (profileId: string | null) =>
    registerReconciled({
      register: input.register,
      readRegistration: input.readRegistration,
      memberId: input.memberId,
      profileId,
      acceptedRulesVersion: input.acceptedRulesVersion,
    });

  if (registrationBody(input.memberId) === null) {
    return { fase: 'iscrizione', profilo: null, iscrizione: await iscrivi(null) };
  }

  const creato = await creaEsincronizzaProfilo({
    createProfile: input.createProfile,
    readProfiles: input.readProfiles,
    syncProfile: input.syncProfile,
    log: input.log,
    giaSincronizzati: input.giaSincronizzati,
    gameId: input.gameId,
  });
  if (creato.fase !== 'creato') return creato;
  return { fase: 'iscrizione', profilo: creato.profilo, iscrizione: await iscrivi(creato.profileId) };
}

/**
 * L'esito del passo «crea il profilo a zero campi e sincronizzalo», senza l'iscrizione (o la coda) che segue.
 *
 * · `profilo`          — la creazione e' fallita (`refused` / `refused-unknown`): chi chiama NON prosegue.
 * · `profilo-senza-id` — il profilo risulta creato (o c'era gia'), ma la risposta non porta un identificativo:
 *                        chi chiama non prosegue (senza `profileId` la scrittura successiva partirebbe nuda).
 * · `creato`           — il profilo c'e', col suo `profileId`, e il sync e' stato chiesto.
 */
export type LimuloCreaProfiloReport =
  | { fase: 'profilo'; profilo: LimuloProfileCreateReport }
  | { fase: 'profilo-senza-id'; profilo: LimuloProfileCreateReport }
  | { fase: 'creato'; profilo: LimuloProfileCreateReport; profileId: string };

/**
 * **Crea il profilo implicito e lo sincronizza** — UN passo, due chiamanti: l'iscrizione a un torneo
 * (`iscriviConProfiloImplicito`) e l'«Entra» di un sit&go (`SitngoCodePanel`). Il profilo e' l'identita' del
 * giocatore per quel gioco, e chi lo crea per entrare in una coda deve farlo come chi lo crea per iscriversi:
 * stesso corpo (`{gameId}`), stessa rilettura dopo un rifiuto (`createProfileReconciled`), stesso sync.
 *
 * 🔴 orc44: il profilo appena creato non ha `meta.provider.sub` finche' il provider non fa il sync — e senza
 * `sub` la partita esterna nasce in `conflict` (`sub_missing`). Si chiede SUBITO, col profileId della creazione,
 * prima di qualunque scrittura che lo usi. ⚠️ Non blocca: `eseguiSync` non lancia e ha un tetto; un rifiuto va
 * nel registro e il passo riesce lo stesso.
 * ⚠️ Qui il Set NON filtra, segna soltanto: e' un atto per tap, non per render, e un profilo appena creato va
 * sincronizzato comunque. Segnarlo evita che l'apertura della scheda (sede 2) lo ripeta nella stessa sessione.
 */
export async function creaEsincronizzaProfilo(input: {
  createProfile: (body: Record<string, string>) => Promise<LimuloCallOutcome<LimuloProfile | null>>;
  /** `GET /v1/games/profiles` — la rilettura dopo un rifiuto della creazione. */
  readProfiles: () => Promise<LimuloCallOutcome<LimuloProfile[]>>;
  /** `POST /v1/games/profiles/:profileId/sync` — orc44, v. `sincronizzaProfilo`. */
  syncProfile: LimuloProfileSync;
  log: (msg: string) => void;
  /** Solo per i test: in produzione e' il Set di modulo. */
  giaSincronizzati?: Set<string>;
  gameId: string;
}): Promise<LimuloCreaProfiloReport> {
  const profilo = await createProfileReconciled({
    createProfile: input.createProfile,
    readProfiles: input.readProfiles,
    gameId: input.gameId,
    // Zero campi resi ⇒ il corpo e' quello che il modulo manderebbe senza campi: `{gameId}`.
    body: profileFormBody(input.gameId, [], {}),
  });
  if (profilo.outcome === 'refused' || profilo.outcome === 'refused-unknown') {
    return { fase: 'profilo', profilo };
  }
  if (profilo.profileId === null) return { fase: 'profilo-senza-id', profilo };

  (input.giaSincronizzati ?? PROFILI_SINCRONIZZATI).add(profilo.profileId);
  await eseguiSync(profilo.profileId, input.syncProfile, input.log);
  return { fase: 'creato', profilo, profileId: profilo.profileId };
}

// ═════════════════════════════════════════════════════════════════════════════
// IL RITIRO, DECISO DAL SERVER (Limulo #99, 2026-09-20)
// ═════════════════════════════════════════════════════════════════════════════

/**
 * Cosa mostra la barra d'azione al posto del ritiro. Quattro esiti, tenuti separati perche'
 * portano a **quattro schermate diverse**, non a quattro sfumature della stessa.
 *
 *  · `attivo`       — il server dice `{open: true}`: bottone acceso, dietro la conferma.
 *  · `bloccato`     — il server dice `{open: false, reason}`: bottone spento, col motivo.
 *  · `sconosciuto`  — il server **non l'ha detto** (o l'ha detto in modo incoerente, che il
 *                     sanitizer scarta allo stesso modo): bottone spento, e lo si dichiara.
 *  · `assente`      — non c'e' niente da ritirare: la barra offre l'iscrizione, non il ritiro.
 */
export type LimuloWithdrawalPlan =
  | { kind: 'attivo' }
  | { kind: 'bloccato'; reason: LimuloWithdrawalReason }
  | { kind: 'sconosciuto' }
  | { kind: 'assente' };

/**
 * 🔴 **SI LEGGE IL VERDETTO DEL SERVER, NON SI DEDUCE DAL CALENDARIO.**
 *
 * Fino al 2026-09-20 questa decisione era `LIMULO_WITHDRAW_AVAILABLE && eligibilita ===
 * 'gia-iscritto'`, cioe' un'intuizione del client su cosa il backend avrebbe accettato. Dal
 * 2026-09-20 il bordo manda `withdrawal` (`policy.js:395-402`), e **si legge quello**.
 *
 * ⛔ Il caso `sconosciuto` **non si chiude su uno degli altri tre**. Riempirlo col calendario
 * — «le iscrizioni sono aperte, quindi il ritiro lo sara'» — rifarebbe il difetto del
 * 2026-09-06 a parti invertite: due verita' opposte nello stesso istante, e un bottone che
 * invita a un rifiuto col 424 muto. «Non lo sappiamo» e' una risposta, e si scrive.
 *
 * ⚠️ `iscritto` entra nella decisione, e **non e' una deduzione**: viene dallo stesso verdetto
 * del server (`registered`), dalla stessa risposta di `withdrawal`. Serve per il caso in cui
 * il server tace sul ritiro *e* l'utente non risulta iscritto: li' non c'e' niente da
 * ritirare, e una riga «non sappiamo se puoi ritirarti» sarebbe rumore su una domanda che
 * nessuno ha posto.
 *
 * ⚠️ `{open: true}` vince **anche su `iscritto` falso**: se il server dice che il ritiro e'
 * aperto, il client non ha titolo per contraddirlo — ed e' esattamente la direzione in cui
 * questa sezione ha deciso di sbagliare, semmai.
 */
export function withdrawalPlan(input: {
  /** Il verdetto letto da `withdrawalOf` (`lib/limulo/model.ts`). */
  withdrawal: { open: true; reason: null } | { open: false; reason: LimuloWithdrawalReason } | null;
  /** `registered` secondo il server, cioe' il verdetto `gia-iscritto`. */
  iscritto: boolean;
}): LimuloWithdrawalPlan {
  if (!LIMULO_WITHDRAW_AVAILABLE) return { kind: 'assente' };
  if (input.withdrawal?.open === true) return { kind: 'attivo' };
  if (input.withdrawal?.open === false) {
    // ⚠️ «Non sei iscritto» non e' un ostacolo da spiegare: e' l'assenza dell'oggetto. Un
    // bottone «Ritirati» spento, con scritto «non sei iscritto», accanto a un bottone
    // «Iscriviti» acceso, e' una schermata che si contraddice da sola.
    return input.withdrawal.reason === 'ritiro.non-iscritto'
      ? { kind: 'assente' }
      : { kind: 'bloccato', reason: input.withdrawal.reason };
  }
  return input.iscritto ? { kind: 'sconosciuto' } : { kind: 'assente' };
}

// ═════════════════════════════════════════════════════════════════════════════
// IL CHECK-IN AL TORNEO, DECISO DAL SERVER (qlash-limulo#108, OPEN al 2026-09-24)
// ═════════════════════════════════════════════════════════════════════════════

/** I motivi per cui il bottone resta SPENTO. `gia-registrato` non c'e': e' una presenza, non un blocco. */
export type LimuloCheckInBlockReason = Exclude<LimuloCheckInReason, 'checkin.gia-registrato'>;

/**
 * Cosa mostra la barra al posto del check-in al torneo. **Sei esiti**, e il primo e' il servito
 * di oggi:
 *
 *  · `in-attesa`   — la busta **non porta** la chiave `checkIn` (oggi, finche' `qlash-limulo#108`
 *                    non e' servita): bottone spento con `register.checkin.blocked`, cioe'
 *                    **esattamente il comportamento di prima**, niente di piu'.
 *  · `nascosto`    — il torneo dichiara di NON pretendere il check-in (`required: false`): non
 *                    c'e' un passo da mostrare (#358, e ADR-0158 §2: a check-in spento all'avvio
 *                    contano presenti tutti gli iscritti attivi).
 *  · `attivo`      — `open: true`: l'UNICO caso in cui il bottone si preme.
 *  · `presente`    — `checkedIn: true` (o il motivo `gia-registrato`): «Presente», con l'ora se
 *                    c'e', e **nessun bottone**.
 *  · `bloccato`    — `open: false` con uno dei tre motivi di blocco: spento, e la frase del motivo.
 *  · `sconosciuto` — la chiave c'e' ma il valore e' `null` (forma incoerente, motivo ignoto — per
 *                    esempio `checkin.attivo-altrove` sul motore 1.68.0): spento, e lo si DICE.
 *
 * ⚠️ Il sesto esito non e' zelo. `register.checkin.blocked` dice «non si puo' ANCORA fare da
 * qui», cioe' descrive un bordo che il check-in non lo porta; con #108 servita quella frase
 * sarebbe falsa, e il `null` del sanitizer va detto per quello che e': «non lo sappiamo».
 */
export type LimuloCheckInPlan =
  | { kind: 'in-attesa' }
  | { kind: 'nascosto' }
  | { kind: 'attivo' }
  | { kind: 'presente'; at: string | null }
  | { kind: 'bloccato'; reason: LimuloCheckInBlockReason }
  | { kind: 'sconosciuto' };

/**
 * 🔴 **IL BOTTONE SI ACCENDE SOLO CON `checkIn.open === true`** — e' la regola del contratto
 * (`PUBLIC_API_CONTRACT.md` @ `qlash-limulo#108`: «Il client mostra il pulsante soltanto con
 * `checkIn.open === true`») e nessun altro ramo porta ad `attivo`.
 *
 * L'ordine e' la logica, non stile:
 * 1. **`required: false` vince su tutto**, anche su `open: true` e su `checkedIn: true`: il
 *    provider non guarda `required` quando decide `open` (`verdettoDelCheckIn`,
 *    `app-qtournaments/app/core/presenze.js` @ `c4ac436`), quindi un torneo che il check-in non
 *    lo pretende puo' dire `open: true` — e un bottone li' scriverebbe una presenza senza effetto.
 * 2. **La presenza prima di `open`**: chi e' gia' presente non deve vedere un invito a premere.
 * 3. Il resto e' `open`, poi il motivo.
 *
 * ⚠️ `required` si legge dal verdetto quando c'e' (stessa risposta di `open`, stesso istante) e
 * dal TORNEO solo quando il verdetto manca: e' il ripiego di #358, che resta valido (ADR-0158).
 *
 * ⚠️ **`finestra-chiusa` NON dice «non hai fatto il check-in».** Il provider calcola `checkedIn`
 * solo a finestra aperta (`checkInDa`: `presenza = verdetto.aperta ? presenzaDi(...) : null`,
 * `presenze.js` @ `c4ac436`): a finestra chiusa chi si era presentato torna `checkedIn: false`.
 * Quindi la copy di quel motivo dice solo che la finestra non e' aperta. [M sul sorgente, non
 * osservato dal vivo.]
 */
export function checkinPlan(input: {
  /** Il verdetto letto da `checkInOf` (`lib/limulo/model.ts`). */
  checkIn: LimuloCheckIn | null;
  /** `bordoPortaCheckIn`: la busta ha la chiave `checkIn` (anche `null`). */
  bordoLoPorta: boolean;
  /** `checkInRequiredOf(torneo)`: `null` = il torneo non l'ha detto. */
  richiestoDalTorneo: boolean | null;
}): LimuloCheckInPlan {
  const c = input.checkIn;
  if (c !== null) {
    if (!c.required) return { kind: 'nascosto' };
    if (c.checkedIn) return { kind: 'presente', at: c.checkedInAt };
    if (c.open) return { kind: 'attivo' };
    // `gia-registrato` con `checkedIn: false` il provider servito non lo produce (lo emette solo a
    // presenza scritta): se arriva, il motivo dice «presente» e la frase giusta e' quella — senza ora.
    if (c.reason === 'checkin.gia-registrato') return { kind: 'presente', at: null };
    return { kind: 'bloccato', reason: c.reason };
  }
  if (input.richiestoDalTorneo === false) return { kind: 'nascosto' };
  return input.bordoLoPorta ? { kind: 'sconosciuto' } : { kind: 'in-attesa' };
}

/**
 * La riga della FINESTRA del check-in (kb#303 §2) si mostra solo dove il check-in e' ancora la domanda
 * (revisione 1 di #485, difetto 1). Lo decidono il piano del server e lo STATO del torneo, mai l'orologio:
 *  · torneo gia' iniziato (`in_progress`, `paused`) ⇒ mai: chi e' ancora iscritto di norma si era presentato
 *    (gli assenti li ritira la chiusura della finestra), ma a finestra chiusa il motore non calcola
 *    `checkedIn` e il piano ricade in `bloccato` — una finestra passata accanto a «Check-in» spento si
 *    leggerebbe «l'hai mancato»;
 *  · `attivo` ⇒ si;
 *  · `bloccato` ⇒ solo `finestra-chiusa` (e' la domanda «quando?»), e `non-iscritto` solo se ci si puo'
 *    ancora iscrivere; mai `parte-non-attiva`, dove il check-in non si puo' fare comunque;
 *  · tutto il resto (presente, nascosto, la barra di prima, sconosciuto) ⇒ no.
 * ⚠️ `check_in_closed` e `ready` escono dal bordo come `created`: da qui sono indistinguibili dal «non ancora
 * aperto», e la riga li' c'e'. Dirli senza guardare l'orologio non si puo'.
 */
export function finestraCheckInDaMostrare(input: {
  piano: LimuloCheckInPlan;
  /** `tournament.status`, lo stato PUBBLICO che manda il bordo. */
  statoTorneo: unknown;
  /** L'iscrizione si puo' ancora fare da qui (la stessa regola della barra). */
  puoIscriversi: boolean;
}): boolean {
  if (torneoGiaAvviato(input.statoTorneo)) return false;
  if (input.piano.kind === 'attivo') return true;
  if (input.piano.kind !== 'bloccato') return false;
  if (input.piano.reason === 'checkin.finestra-chiusa') return true;
  if (input.piano.reason === 'checkin.non-iscritto') return input.puoIscriversi;
  return false;
}

/** Una frase per motivo di blocco. `Record` COMPLETO: un motivo nuovo senza frase e' un errore di build. */
export const CHECKIN_BLOCK_COPY: Record<LimuloCheckInBlockReason, string> = {
  'checkin.non-iscritto': 'register.checkin.reason.nonIscritto',
  'checkin.parte-non-attiva': 'register.checkin.reason.parteNonAttiva',
  'checkin.finestra-chiusa': 'register.checkin.reason.finestraChiusa',
};

/**
 * L'esito del check-in al torneo, dopo la rilettura. Stessa forma del ritiro, e senza
 * `identity-missing` per la stessa ragione: la POST parte **senza corpo** e la parte la deriva il
 * server dall'attore (`router.js:2223-2249` @ `c4ac436`, `source: {}`).
 */
export type LimuloCheckinOutcome =
  /** POST accettata **e** la rilettura dice `checkedIn: true`. L'unico «sei presente». */
  | 'present'
  /** POST accettata ma la rilettura non lo conferma (o non ha risposto). */
  | 'submitted-unconfirmed'
  /** POST rifiutata, ma la rilettura ti da' presente: era una corsa o un doppio tap. */
  | 'already-present'
  /** POST rifiutata e la rilettura dice che NON risulti presente. */
  | 'refused'
  /** POST rifiutata e la rilettura non e' riuscita: il dubbio vero. */
  | 'refused-unknown';

export interface LimuloCheckinReport {
  outcome: LimuloCheckinOutcome;
  /** Il rifiuto della POST, se c'e' stato — anche quando la rilettura lo ha poi smentito. */
  failure: LimuloFailure | null;
  /**
   * Il motivo che la RILETTURA dichiara dopo un rifiuto (`checkIn.reason`), se e' uno dei tre di
   * blocco. E' il modo di dare un nome a un rifiuto che il 424 lascia muto: non si deduce dal
   * rifiuto, si legge da cio' che il server HA adesso.
   */
  blockReason: LimuloCheckInBlockReason | null;
}

/**
 * Il check-in al torneo, con la rilettura SEMPRE — anche dopo un 2xx.
 *
 * ⚠️ La risposta della POST ha gia' la forma della GET (`sanitizeRegistrationStatus`), eppure si
 * rilegge lo stesso, per la ragione di `withdrawReconciled`: il ramo che conta e' il RIFIUTO, che
 * puo' arrivare muto (424); e una funzione che rilegge «solo quando serve» ha due percorsi da
 * provare invece di uno. **Dopo un rifiuto si rilegge e si dice cosa il server HA.**
 */
export async function checkinReconciled(input: {
  checkin: () => Promise<LimuloCallOutcome<LimuloRegistrationStatus | null>>;
  /** `GET /v1/tournaments/:id/registration` — la fonte di `checkIn` dopo il fatto. */
  readRegistration: () => Promise<LimuloCallOutcome<LimuloRegistrationStatus | null>>;
}): Promise<LimuloCheckinReport> {
  const esito = await input.checkin();
  const errore = outcomeError(esito);

  const riletto = await input.readRegistration();
  const stato = outcomeData(riletto);
  // Stessa regola dell'iscrizione e del ritiro: rilettura fallita e 2xx vuoto sono entrambi «il
  // server non ha detto niente» — e un verdetto illeggibile (`checkInOf` → `null`) pure: una
  // busta senza `checkIn` non dice niente sulla presenza, quindi non e' un «no».
  const verdetto = stato !== undefined && stato !== null ? checkInOf(stato) : null;
  // «Presente» come lo decide `checkinPlan`: il flag, o il motivo che dice presenza gia' scritta.
  const presente =
    verdetto !== null && (verdetto.checkedIn || verdetto.reason === 'checkin.gia-registrato');

  if (errore === undefined) {
    return { outcome: presente ? 'present' : 'submitted-unconfirmed', failure: null, blockReason: null };
  }

  const failure = limuloFailure(errore);
  if (verdetto === null) return { outcome: 'refused-unknown', failure, blockReason: null };
  if (presente) return { outcome: 'already-present', failure, blockReason: null };
  const blockReason =
    verdetto.open === false && verdetto.reason !== 'checkin.gia-registrato' ? verdetto.reason : null;
  // ⚠️ `refused` NON vuol dire «il server dice che non sei presente»: a finestra chiusa il provider
  // riporta `checkedIn: false` anche a chi si era presentato (v. `checkinPlan`). La copy non lo dice.
  return { outcome: 'refused', failure, blockReason };
}
